import { useState, useEffect, useMemo } from 'react';
import { getBackendUrl } from '../../src/config/config.loader';
import { usePermission } from '../../src/utils/permissions';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { usePagination } from '../../hooks/usePagination';
import { Pagination } from '../common/Pagination';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import {
  Calculator,
  Target,
  Gavel,
  Plus,
  Search,
  FileText,
  DollarSign,
  TrendingUp,
  Upload,
  Download,
  Filter,
  Eye,
  Edit,
  Trash2,
  Calendar,
  Building,
  User,
  MapPin,
  ChevronDown,
  Link,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  Save,
  X,
  Loader2
} from 'lucide-react';
import { preConstructionService } from '../../src/services/pre-construction.service';
import { authService } from '../../src/services/auth.service';
import { httpClient } from '../../src/services/http.client';
import { procurementService } from '../../src/services/procurement.service';
import { useOrgCurrency } from '../../src/hooks/useOrgCurrency';
import { CURRENCIES, formatCurrency, formatCurrencyCompact } from '../../src/utils/currency';
import { DocumentList } from '../common/DocumentDownload';
import { showSuccess, showError } from '../../src/utils/notification.utils';
import { useAuth } from '../../src/hooks/useAuth';
import { useBids } from '../../src/hooks/queries/use-bids';
import { useBoqs, useBoqItems } from '../../src/hooks/queries/use-boqs';
import { useEstimates, useEstimateItems } from '../../src/hooks/queries/use-estimates';

// Unit type mapping helper
const getReadableUnit = (unit: string): string => {
  const unitMap: { [key: string]: string } = {
    'each': 'Each',
    'lf': 'Linear Meter',
    'sf': 'Square Meter',
    'cf': 'Cubic Meter',
    'sy': 'Square Meter',
    'cy': 'Cubic Meter',
    'lbs': 'Kilogram',
    'tons': 'Tons',
    'hours': 'Hours',
    'days': 'Days',
    'ls': 'Lump Sum',
    // Additional units from the UI
    'm³': 'Cubic Meter',
    'm²': 'Square Meter',
    'm': 'Linear Meter',
    'kg': 'Kilogram',
    'ton': 'Tons',
    'hr': 'Hours',
    'LS': 'Lump Sum'
  };
  return unitMap[unit] || unit;
};

// Normalize unit values to backend enum values
const normalizeUnitValue = (unit: string): string => {
  // Map old metric units to backend enum values
  const normalizationMap: { [key: string]: string } = {
    'm³': 'cf',
    'm²': 'sf',
    'm': 'lf',
    'kg': 'lbs',
    'ton': 'tons',
    'hr': 'hours',
    'LS': 'ls'
  };
  return normalizationMap[unit] || unit;
};

// Alert Modal Component
interface AlertModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  message: string;
  type: 'success' | 'error';
}

function AlertModal({ isOpen, onClose, title, message, type }: AlertModalProps) {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {type === 'success' ? (
              <CheckCircle className="h-5 w-5 text-green-600" />
            ) : (
              <XCircle className="h-5 w-5 text-red-600" />
            )}
            {title}
          </DialogTitle>
        </DialogHeader>
        <div className="py-4">
          <p className="text-sm text-muted-foreground">{message}</p>
        </div>
        <DialogFooter>
          <Button onClick={onClose} className={type === 'success' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}>
            OK
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Import BOQ Modal Component
function ImportBOQModal() {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="outline">
          <Upload className="h-4 w-4 mr-2" />
          Import BOQ
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[600px] modal-content">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5" />
            Import BOQ Document
          </DialogTitle>
          <DialogDescription>
            Upload BOQ files or use our templates to get started quickly
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-6 py-4">
          {/* Template Downloads */}
          <div className="space-y-3">
            <Label>Download Templates</Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Button variant="outline" className="justify-between">
                <span>Excel Template</span>
                <Download className="h-4 w-4" />
              </Button>
              <Button variant="outline" className="justify-between">
                <span>CSV Template</span>
                <Download className="h-4 w-4" />
              </Button>
            </div>
          </div>
          
          {/* File Upload */}
          <div className="space-y-3">
            <Label>Upload BOQ File</Label>
            <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-6">
              <div className="text-center space-y-2">
                <Upload className="h-8 w-8 mx-auto text-muted-foreground" />
                <div className="space-y-1">
                  <p className="text-sm font-medium">Drop your BOQ file here, or click to browse</p>
                  <p className="text-xs text-muted-foreground">
                    Supports: .xlsx, .xls, .csv, .pdf (max 10MB)
                  </p>
                </div>
                <Button variant="outline" size="sm">
                  Choose File
                </Button>
              </div>
            </div>
          </div>
          
          {/* Import Settings */}
          <div className="space-y-4">
            <Label>Import Settings</Label>
            <div className="space-y-3">
              <div className="space-y-2">
                <Label htmlFor="linked-bid">Link to Bid (Optional)</Label>
                <Select>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a bid" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bid-1">Highway Bridge Construction</SelectItem>
                    <SelectItem value="bid-2">Hospital Expansion Phase 2</SelectItem>
                    <SelectItem value="bid-3">Industrial Park Infrastructure</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex items-center space-x-2">
                <input type="checkbox" id="auto-optimize" defaultChecked />
                <Label htmlFor="auto-optimize">Enable AI optimization during import</Label>
              </div>
              
              <div className="flex items-center space-x-2">
                <input type="checkbox" id="validate-rates" defaultChecked />
                <Label htmlFor="validate-rates">Validate rates against market data</Label>
              </div>
            </div>
          </div>
        </div>
        
        <DialogFooter>
          <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
          <Button>Import BOQ</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// New BOQ Modal Component
function NewBOQModal({ onSuccess }: { onSuccess?: () => void }) {
  const orgCurrency = useOrgCurrency();
  const [linkedBid, setLinkedBid] = useState('');
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [bids, setBids] = useState<any[]>([]);
  const [loadingBids, setLoadingBids] = useState(false);
  const [formData, setFormData] = useState({
    boq_number: '',
    boq_name: '',
    description: '',
    version: '1.0',
    project_name: '',
    project_location: '',
    client_name: '',
    total_amount: 0,
    contingency_percentage: 0,
    contingency_amount: 0,
    final_amount: 0,
    currency: orgCurrency,
    exchange_rate: 1.0,
    status: 'draft',
    preparation_date: '',
    validity_period: 3,
    bid_id: ''
  });

  const showProjectFields = linkedBid === 'none' || !linkedBid;

  // Fetch bids when modal opens
  useEffect(() => {
    const fetchBids = async () => {
      if (!open) return;

      setLoadingBids(true);
      try {
        const userProfile = await authService.getUserProfile();
        const organizationId = userProfile.organizationId;

        if (import.meta.env.DEV) {
          console.log('Fetching bids for BOQ link:', { organization_id: organizationId });
        }

        const response = await httpClient.get(`/api/v1/bids?organization_id=${organizationId}`);

        if (import.meta.env.DEV) {
          console.log('Bids loaded for BOQ:', response.data);
        }

        setBids(response.data || []);
      } catch (error) {
        console.error('Failed to fetch bids:', error);
      } finally {
        setLoadingBids(false);
      }
    };

    fetchBids();
  }, [open]);

  const handleSave = async () => {
    // Validate required fields
    if (!formData.boq_name || !formData.boq_number) {
      showError({
        title: 'Validation Error',
        message: 'Please fill in required fields: BOQ Name and BOQ Number'
      });
      return;
    }

    setSaving(true);

    try {
      // Get organization_id from user profile
      const userProfile = await authService.getUserProfile();
      const organizationId = userProfile.organizationId;

      if (!organizationId) {
        throw new Error('Organization ID not found. Please contact support.');
      }

      // Prepare payload
      const payload = {
        boq_number: formData.boq_number,
        boq_name: formData.boq_name,
        description: formData.description,
        version: formData.version,
        project_name: showProjectFields ? formData.project_name : undefined,
        project_location: showProjectFields ? formData.project_location : undefined,
        client_name: showProjectFields ? formData.client_name : undefined,
        total_amount: formData.total_amount,
        contingency_percentage: formData.contingency_percentage,
        contingency_amount: formData.contingency_amount,
        final_amount: formData.final_amount,
        currency: formData.currency,
        exchange_rate: formData.exchange_rate,
        status: formData.status,
        preparation_date: formData.preparation_date || null,
        validity_period: formData.validity_period,
        bid_id: linkedBid && linkedBid !== 'none' ? linkedBid : null
      };

      if (import.meta.env.DEV) {
        console.log('Creating BOQ with payload:', JSON.stringify(payload, null, 2));
        console.log('Organization ID:', organizationId);
      }

      // Use httpClient which handles authentication automatically
      await httpClient.post(`/api/v1/boqs?organization_id=${organizationId}`, payload);

      // Reset form
      setFormData({
        boq_number: '',
        boq_name: '',
        description: '',
        version: '1.0',
        project_name: '',
        project_location: '',
        client_name: '',
        total_amount: 0,
        contingency_percentage: 0,
        contingency_amount: 0,
        final_amount: 0,
        currency: orgCurrency,
        exchange_rate: 1.0,
        status: 'draft',
        preparation_date: '',
        validity_period: 3,
        bid_id: ''
      });
      setLinkedBid('');
      setOpen(false);

      showSuccess({
        title: 'BOQ Created',
        message: 'BOQ created successfully!'
      });

      if (onSuccess) {
        onSuccess();
      }

    } catch (error) {
      console.error('Error creating BOQ:', error);
      showError({
        title: 'Creation Failed',
        message: `Failed to create BOQ: ${error instanceof Error ? error.message : 'Unknown error'}`
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>
          <Plus className="h-4 w-4 mr-2" />
          New BOQ
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto modal-content">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus className="h-5 w-5" />
            Create New BOQ
          </DialogTitle>
          <DialogDescription>
            Create a new Bill of Quantities for your project
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="boq-name">BOQ Name</Label>
              <Input
                id="boq-name"
                placeholder="e.g., Residential Complex Alpha"
                value={formData.boq_name}
                onChange={(e) => setFormData({...formData, boq_name: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="boq-number">BOQ Number</Label>
              <Input
                id="boq-number"
                placeholder="e.g., BOQ-2024-001"
                value={formData.boq_number}
                onChange={(e) => setFormData({...formData, boq_number: e.target.value})}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="version">Version</Label>
              <Input
                id="version"
                type="number"
                step="0.1"
                placeholder="1.0"
                value={formData.version}
                onChange={(e) => setFormData({...formData, version: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="preparation-date">Preparation Date</Label>
              <Input
                id="preparation-date"
                type="date"
                value={formData.preparation_date}
                onChange={(e) => setFormData({...formData, preparation_date: e.target.value})}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="linked-bid">Link to Bid</Label>
            <Select value={linkedBid} onValueChange={setLinkedBid} disabled={loadingBids}>
              <SelectTrigger>
                <SelectValue placeholder={loadingBids ? "Loading bids..." : "Select a bid"} />
              </SelectTrigger>
              <SelectContent>
                {bids.filter(bid => bid.bid_id || bid.id).map((bid) => (
                  <SelectItem key={bid.bid_id || bid.id} value={bid.bid_id || bid.id}>
                    {bid.project_name || bid.project || 'Unnamed Project'}
                  </SelectItem>
                ))}
                <SelectItem value="none">No bid linked</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Show these fields only when no bid is linked */}
          {showProjectFields && (
            <div className="space-y-4 p-4 border rounded-lg bg-muted/30">
              <p className="text-sm font-medium">Project Information</p>
              <div className="space-y-2">
                <Label htmlFor="project-name">Project Name</Label>
                <Input
                  id="project-name"
                  placeholder="Enter project name"
                  value={formData.project_name}
                  onChange={(e) => setFormData({...formData, project_name: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="project-location">Project Location</Label>
                <Input
                  id="project-location"
                  placeholder="Enter project location"
                  value={formData.project_location}
                  onChange={(e) => setFormData({...formData, project_location: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="client-name">Client Name</Label>
                <Input
                  id="client-name"
                  placeholder="Enter client name"
                  value={formData.client_name}
                  onChange={(e) => setFormData({...formData, client_name: e.target.value})}
                />
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              placeholder="Describe the scope and nature of this BOQ..."
              rows={3}
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="currency">Currency</Label>
              <Select
                value={formData.currency}
                onValueChange={(value) => setFormData({...formData, currency: value})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CURRENCIES.map(c => <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="exchange-rate">Exchange Rate</Label>
              <Input
                id="exchange-rate"
                type="number"
                step="0.01"
                placeholder="1.0"
                value={formData.exchange_rate}
                onChange={(e) => setFormData({...formData, exchange_rate: parseFloat(e.target.value) || 0})}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="contingency-percentage">Contingency (%)</Label>
              <Input
                id="contingency-percentage"
                type="number"
                step="0.1"
                placeholder="5.0"
                value={formData.contingency_percentage}
                onChange={(e) => setFormData({...formData, contingency_percentage: parseFloat(e.target.value) || 0})}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="contingency-amount">Contingency Amount</Label>
              <Input
                id="contingency-amount"
                type="number"
                step="0.01"
                placeholder="0.00"
                value={formData.contingency_amount}
                onChange={(e) => setFormData({...formData, contingency_amount: parseFloat(e.target.value) || 0})}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="final-amount">Final Amount</Label>
              <Input
                id="final-amount"
                type="number"
                step="0.01"
                placeholder="0.00"
                value={formData.final_amount}
                onChange={(e) => setFormData({...formData, final_amount: parseFloat(e.target.value) || 0})}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="validity-period">Validity (Months)</Label>
              <Input
                id="validity-period"
                type="number"
                placeholder="3"
                value={formData.validity_period}
                onChange={(e) => setFormData({...formData, validity_period: parseInt(e.target.value) || 0})}
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? 'Creating...' : 'Create BOQ'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Add BOQ Item Modal Component with Search
function AddBOQItemModal({ boqId, onSuccess }: { boqId?: string; onSuccess?: () => void }) {
  const { orgId } = useAuth();
  const [searchExisting, setSearchExisting] = useState("");
  const [showCreateNew, setShowCreateNew] = useState(false);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showCostBreakdown, setShowCostBreakdown] = useState(false);
  const [existingItems, setExistingItems] = useState<any[]>([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [formData, setFormData] = useState({
    item_number: '',
    description: '',
    specification: '',
    unit_of_measure: 'each',
    quantity: 0,
    unit_rate: 0,
    work_category: 'sitework',
    remarks: '',
    drawing_reference: '',
    labor_cost: 0,
    material_cost: 0,
    equipment_cost: 0,
    overhead_percentage: 0,
    profit_percentage: 0,
    parent_item_id: '',
    level: 1
  });

  // Search procurement item catalog
  useEffect(() => {
    const searchProcurementItems = async () => {
      if (!searchExisting || searchExisting.length < 2 || !orgId) {
        setExistingItems([]);
        return;
      }

      setLoadingItems(true);
      try {
        const response = await procurementService.searchProcurementItems(searchExisting, orgId);
        const items = response.data ?? [];

        const transformedItems = items.map((item: any) => ({
          id: item.item_id,
          code: item.item_code,
          description: item.item_name + (item.description ? ` — ${item.description}` : ''),
          unit: normalizeUnitValue(item.unit_of_measure),
          category: item.category,
          rate: parseFloat(item.standard_unit_price) || 0,
          specification: item.specifications ? JSON.stringify(item.specifications) : '',
          ...item
        }));

        setExistingItems(transformedItems);
      } catch (error: any) {
        console.error('Failed to search procurement items:', error);
        setExistingItems([]);
      } finally {
        setLoadingItems(false);
      }
    };

    const debounceTimer = setTimeout(() => {
      searchProcurementItems();
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [searchExisting, orgId]);

  const totalAmount = formData.quantity * formData.unit_rate;

  const handleSelectExisting = (item: any) => {
    setFormData({
      ...formData,
      item_number: item.code || item.item_code || '',
      description: item.item_name || item.description || '',
      specification: item.specification || '',
      unit_of_measure: item.unit || 'each',
      unit_rate: item.rate,
      work_category: item.category || 'sitework'
    });
    setSearchExisting("");
    setShowCreateNew(true);
  };

  const handleSave = async () => {
    if (!formData.item_number || !formData.description || !formData.unit_of_measure) {
      showError({
        title: 'Validation Error',
        message: 'Please fill in required fields: Item Code, Description, and Unit of Measure'
      });
      return;
    }

    if (!boqId) {
      showError({
        title: 'No BOQ Selected',
        message: 'Please select a BOQ first.'
      });
      return;
    }

    setSaving(true);
    try {
      const payload: any = {
        item_number: formData.item_number,
        description: formData.description,
        specification: formData.specification || '',
        work_category: formData.work_category,
        quantity: formData.quantity,
        unit_of_measure: formData.unit_of_measure,
        unit_rate: formData.unit_rate,
        total_amount: totalAmount,
        labor_cost: formData.labor_cost,
        material_cost: formData.material_cost,
        equipment_cost: formData.equipment_cost,
        overhead_percentage: formData.overhead_percentage,
        profit_percentage: formData.profit_percentage,
        remarks: formData.remarks || '',
        drawing_reference: formData.drawing_reference || '',
        level: formData.level,
        boq_id: boqId
      };

      // Only include parent_item_id if it's provided
      if (formData.parent_item_id) {
        payload.parent_item_id = formData.parent_item_id;
      }

      if (import.meta.env.DEV) {
        console.log('=== Saving BOQ Item ===');
        console.log('BOQ ID:', boqId);
        console.log('Full Payload:', JSON.stringify(payload, null, 2));
        console.log('Total Amount Calculated:', totalAmount);
      }

      await httpClient.post(`/api/v1/boqs/${boqId}/items`, payload);

      // Change BOQ status from draft to in_progress after adding item
      try {
        await httpClient.post(`/api/v1/boqs/${boqId}/status`, { status: 'in_progress' });
      } catch (statusError) {
        console.error('Failed to update BOQ status:', statusError);
        // Don't fail the whole operation if status update fails
      }

      showSuccess({
        title: 'Item Added',
        message: 'BOQ item added successfully!'
      });

      setOpen(false);
      // Reset form
      setFormData({
        item_number: '',
        description: '',
        specification: '',
        unit_of_measure: 'each',
        quantity: 0,
        unit_rate: 0,
        work_category: 'sitework',
        remarks: '',
        drawing_reference: '',
        labor_cost: 0,
        material_cost: 0,
        equipment_cost: 0,
        overhead_percentage: 0,
        profit_percentage: 0,
        parent_item_id: '',
        level: 1
      });
      setShowCreateNew(false);

      if (onSuccess) {
        onSuccess();
      }
    } catch (error: any) {
      console.error('Failed to save BOQ item:', error);
      showError({
        title: 'Save Failed',
        message: error.message || 'Failed to save BOQ item. Please try again.'
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>
          <Plus className="h-4 w-4 mr-2" />
          Add Item
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto modal-content">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus className="h-5 w-5" />
            Add BOQ Item
          </DialogTitle>
          <DialogDescription>
            Search existing items or create a new item for the BOQ
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Search Existing Items */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label>Search Existing Items</Label>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowCreateNew(true)}
              >
                Create New Item
              </Button>
            </div>

            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by description or code..."
                className="pl-9"
                value={searchExisting}
                onChange={(e) => setSearchExisting(e.target.value)}
              />
            </div>

            {searchExisting && searchExisting.length >= 2 && (
              <div className="border rounded-lg max-h-48 overflow-y-auto">
                {loadingItems ? (
                  <div className="p-4 text-center text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin inline-block mr-2" />
                    Searching...
                  </div>
                ) : existingItems.length > 0 ? (
                  existingItems.map((item) => (
                    <div key={item.id} className="p-3 border-b last:border-b-0 hover:bg-muted/50 cursor-pointer">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-sm text-muted-foreground">{item.code}</span>
                            <Badge variant="outline" className="text-xs">{item.category}</Badge>
                          </div>
                          <p className="font-medium">{item.description}</p>
                          <p className="text-sm text-muted-foreground">
                            Unit: {getReadableUnit(item.unit)} | Rate: ${item.rate.toFixed(2)}
                          </p>
                        </div>
                        <Button size="sm" onClick={() => handleSelectExisting(item)}>
                          Select
                        </Button>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="p-4 text-center text-muted-foreground">
                    No existing items found.
                    <Button variant="link" className="p-0 h-auto" onClick={() => setShowCreateNew(true)}>
                      Create new item
                    </Button>
                  </div>
                )}
              </div>
            )}
            {searchExisting && searchExisting.length < 2 && (
              <div className="text-sm text-muted-foreground">
                Type at least 2 characters to search...
              </div>
            )}
          </div>

          {/* Create New Item Form */}
          {(showCreateNew || !searchExisting) && (
            <div className="space-y-4 border-t pt-4">
              <div className="flex items-center justify-between">
                <Label>Create New Item</Label>
                {showCreateNew && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowCreateNew(false)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="item-code">Item Code</Label>
                  <Input
                    id="item-code"
                    placeholder="e.g., EXC-001"
                    value={formData.item_number}
                    onChange={(e) => setFormData({...formData, item_number: e.target.value})}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="item-description">Item Description</Label>
                  <Textarea
                    id="item-description"
                    placeholder="Detailed description of the work item..."
                    rows={2}
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                  />
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="unit">Unit of Measure</Label>
                    <Select value={formData.unit_of_measure} onValueChange={(value) => setFormData({...formData, unit_of_measure: value})}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select unit" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="each">Each</SelectItem>
                        <SelectItem value="lf">Linear Meter</SelectItem>
                        <SelectItem value="sf">Square Meter</SelectItem>
                        <SelectItem value="cf">Cubic Meter</SelectItem>
                        <SelectItem value="sy">Square Meter</SelectItem>
                        <SelectItem value="cy">Cubic Meter</SelectItem>
                        <SelectItem value="lbs">Kilogram</SelectItem>
                        <SelectItem value="tons">Tons</SelectItem>
                        <SelectItem value="hours">Hours</SelectItem>
                        <SelectItem value="days">Days</SelectItem>
                        <SelectItem value="ls">Lump Sum</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="quantity">Quantity</Label>
                    <Input
                      id="quantity"
                      type="number"
                      placeholder="0"
                      value={formData.quantity}
                      onChange={(e) => setFormData({...formData, quantity: parseFloat(e.target.value) || 0})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="rate">Unit Rate</Label>
                    <Input
                      id="rate"
                      type="number"
                      placeholder="0.00"
                      step="0.01"
                      value={formData.unit_rate}
                      onChange={(e) => setFormData({...formData, unit_rate: parseFloat(e.target.value) || 0})}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="total-amount">Total Amount</Label>
                  <Input
                    id="total-amount"
                    type="number"
                    value={totalAmount.toFixed(2)}
                    readOnly
                    className="bg-muted"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="category">Work Category</Label>
                  <Select value={formData.work_category} onValueChange={(value) => setFormData({...formData, work_category: value})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select work category" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sitework">Sitework</SelectItem>
                      <SelectItem value="concrete">Concrete</SelectItem>
                      <SelectItem value="masonry">Masonry</SelectItem>
                      <SelectItem value="metals">Metals</SelectItem>
                      <SelectItem value="wood_plastics">Wood & Plastics</SelectItem>
                      <SelectItem value="thermal_moisture">Thermal & Moisture Protection</SelectItem>
                      <SelectItem value="openings">Openings</SelectItem>
                      <SelectItem value="finishes">Finishes</SelectItem>
                      <SelectItem value="specialties">Specialties</SelectItem>
                      <SelectItem value="equipment">Equipment</SelectItem>
                      <SelectItem value="furnishings">Furnishings</SelectItem>
                      <SelectItem value="conveying">Conveying Systems</SelectItem>
                      <SelectItem value="fire_suppression">Fire Suppression</SelectItem>
                      <SelectItem value="plumbing">Plumbing</SelectItem>
                      <SelectItem value="hvac">HVAC</SelectItem>
                      <SelectItem value="electrical">Electrical</SelectItem>
                      <SelectItem value="communications">Communications</SelectItem>
                      <SelectItem value="electronic_safety">Electronic Safety & Security</SelectItem>
                      <SelectItem value="earthwork">Earthwork</SelectItem>
                      <SelectItem value="utilities">Utilities</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="cost-breakdown"
                    checked={showCostBreakdown}
                    onChange={(e) => setShowCostBreakdown(e.target.checked)}
                    className="h-4 w-4"
                  />
                  <Label htmlFor="cost-breakdown" className="cursor-pointer">
                    Add Cost Breakdown Components
                  </Label>
                </div>

                {showCostBreakdown && (
                  <div className="space-y-4 border-t pt-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="labor-cost">Labor Cost</Label>
                        <Input
                          id="labor-cost"
                          type="number"
                          step="0.01"
                          value={formData.labor_cost}
                          onChange={(e) => setFormData({...formData, labor_cost: parseFloat(e.target.value) || 0})}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="material-cost">Material Cost</Label>
                        <Input
                          id="material-cost"
                          type="number"
                          step="0.01"
                          value={formData.material_cost}
                          onChange={(e) => setFormData({...formData, material_cost: parseFloat(e.target.value) || 0})}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="equipment-cost">Equipment Cost</Label>
                        <Input
                          id="equipment-cost"
                          type="number"
                          step="0.01"
                          value={formData.equipment_cost}
                          onChange={(e) => setFormData({...formData, equipment_cost: parseFloat(e.target.value) || 0})}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="overhead-percentage">Overhead %</Label>
                        <Input
                          id="overhead-percentage"
                          type="number"
                          step="0.01"
                          value={formData.overhead_percentage}
                          onChange={(e) => setFormData({...formData, overhead_percentage: parseFloat(e.target.value) || 0})}
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="profit-percentage">Profit %</Label>
                      <Input
                        id="profit-percentage"
                        type="number"
                        step="0.01"
                        value={formData.profit_percentage}
                        onChange={(e) => setFormData({...formData, profit_percentage: parseFloat(e.target.value) || 0})}
                      />
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="specification">Specification (Optional)</Label>
                  <Textarea
                    id="specification"
                    placeholder="Technical specifications..."
                    rows={2}
                    value={formData.specification}
                    onChange={(e) => setFormData({...formData, specification: e.target.value})}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="remarks">Remarks (Optional)</Label>
                    <Input
                      id="remarks"
                      placeholder="Additional remarks..."
                      value={formData.remarks}
                      onChange={(e) => setFormData({...formData, remarks: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="drawing_reference">Drawing Reference (Optional)</Label>
                    <Input
                      id="drawing_reference"
                      placeholder="e.g., A-101"
                      value={formData.drawing_reference}
                      onChange={(e) => setFormData({...formData, drawing_reference: e.target.value})}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Add Item'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// BOQ Details Modal (View/Edit)
function BOQDetailsModal({ mode, boq, trigger, onSuccess }: { mode: 'view' | 'edit'; boq: any; trigger: React.ReactNode; onSuccess?: () => void }) {
  const orgCurrency = useOrgCurrency();
  const isReadOnly = mode === 'view';
  const [errors, setErrors] = useState<{[key: string]: string}>({});
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const [linkedBid, setLinkedBid] = useState(boq?.bid_id || '');
  const [showProjectFields, setShowProjectFields] = useState(boq?.bid_id === 'none' || !boq?.bid_id);
  const [bids, setBids] = useState<any[]>([]);
  const [loadingBids, setLoadingBids] = useState(false);
  const [formData, setFormData] = useState({
    boq_number: boq?.boq_number || '',
    boq_name: boq?.boq_name || '',
    description: boq?.description || '',
    version: boq?.version || '1.0',
    project_name: boq?.project_name || '',
    project_location: boq?.project_location || '',
    client_name: boq?.client_name || '',
    total_amount: boq?.total_amount || 0,
    contingency_percentage: boq?.contingency_percentage || 0,
    contingency_amount: boq?.contingency_amount || 0,
    final_amount: boq?.final_amount || 0,
    currency: boq?.currency || orgCurrency,
    exchange_rate: boq?.exchange_rate || 1.0,
    status: boq?.status || 'draft',
    preparation_date: boq?.preparation_date || '',
    validity_period: boq?.validity_period || 3,
    bid_id: boq?.bid_id || ''
  });

  // Fetch bids when modal opens
  useEffect(() => {
    const fetchBids = async () => {
      if (!open) return;

      setLoadingBids(true);
      try {
        const userProfile = await authService.getUserProfile();
        const organizationId = userProfile.organizationId;

        const response = await httpClient.get(`/api/v1/bids?organization_id=${organizationId}`);
        setBids(response.data || []);
      } catch (error) {
        console.error('Failed to fetch bids:', error);
      } finally {
        setLoadingBids(false);
      }
    };

    fetchBids();
  }, [open]);

  const handleSave = async () => {
    if (!formData.boq_name || !formData.boq_number) {
      showError({
        title: 'Validation Error',
        message: 'Please fill in required fields: BOQ Name and BOQ Number'
      });
      return;
    }

    setSaving(true);

    try {
      const userProfile = await authService.getUserProfile();
      const organizationId = userProfile.organizationId;

      if (!organizationId) {
        throw new Error('Organization ID not found. Please contact support.');
      }

      const payload = {
        boq_number: formData.boq_number,
        boq_name: formData.boq_name,
        description: formData.description,
        version: formData.version,
        project_name: showProjectFields ? formData.project_name : undefined,
        project_location: showProjectFields ? formData.project_location : undefined,
        client_name: showProjectFields ? formData.client_name : undefined,
        total_amount: formData.total_amount,
        contingency_percentage: formData.contingency_percentage,
        contingency_amount: formData.contingency_amount,
        final_amount: formData.final_amount,
        currency: formData.currency,
        exchange_rate: formData.exchange_rate,
        status: formData.status,
        preparation_date: formData.preparation_date || null,
        validity_period: formData.validity_period,
        bid_id: linkedBid && linkedBid !== 'none' ? linkedBid : null
      };

      if (import.meta.env.DEV) {
        console.log('Updating BOQ with payload:', JSON.stringify(payload, null, 2));
      }

      await httpClient.put(`/api/v1/boqs/${boq?.id}?organization_id=${organizationId}`, payload);

      // TODO: When a new Project is created and linked to this BOQ,
      // the BOQ status should be updated to 'final' via:
      // await httpClient.post(`/api/v1/boqs/${boq?.id}/status`, { status: 'final' });
      // This should be implemented in the Project creation flow.

      showSuccess({
        title: 'BOQ Updated',
        message: 'BOQ updated successfully!'
      });

      setOpen(false);

      if (onSuccess) {
        onSuccess();
      }

    } catch (error) {
      console.error('Error updating BOQ:', error);
      showError({
        title: 'Update Failed',
        message: `Failed to update BOQ: ${error instanceof Error ? error.message : 'Unknown error'}`
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto modal-content">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {mode === 'view' ? <Eye className="h-5 w-5" /> : <Edit className="h-5 w-5" />}
            {mode === 'view' ? 'View BOQ Details' : 'Edit BOQ Details'}
          </DialogTitle>
          <DialogDescription>
            {mode === 'view' ? 'View complete BOQ information' : 'Update BOQ information'}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="boq-name">BOQ Name</Label>
              <Input
                id="boq-name"
                value={formData.boq_name}
                onChange={(e) => !isReadOnly && setFormData({...formData, boq_name: e.target.value})}
                readOnly={isReadOnly}
                className={isReadOnly ? 'bg-muted' : ''}
                placeholder="e.g., Residential Complex Alpha"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="boq-number">BOQ Number</Label>
              <Input
                id="boq-number"
                value={formData.boq_number}
                onChange={(e) => !isReadOnly && setFormData({...formData, boq_number: e.target.value})}
                readOnly={isReadOnly}
                className={isReadOnly ? 'bg-muted' : ''}
                placeholder="e.g., BOQ-2024-001"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="version">Version</Label>
              <Input
                id="version"
                type="number"
                step="0.1"
                value={formData.version}
                onChange={(e) => !isReadOnly && setFormData({...formData, version: e.target.value})}
                readOnly={isReadOnly}
                className={isReadOnly ? 'bg-muted' : ''}
                placeholder="1.0"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="preparation-date">Preparation Date</Label>
              <Input
                id="preparation-date"
                type="date"
                value={formData.preparation_date}
                onChange={(e) => !isReadOnly && setFormData({...formData, preparation_date: e.target.value})}
                readOnly={isReadOnly}
                className={isReadOnly ? 'bg-muted' : ''}
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="linked-bid">Link to Bid</Label>
            {isReadOnly ? (
              <Input
                id="linked-bid"
                value={bids.find(b => b.bid_id === formData.bid_id || b.id === formData.bid_id)?.project_name || boq?.bid || formData.bid_id || 'No bid linked'}
                readOnly={isReadOnly}
                className="bg-muted"
              />
            ) : (
              <Select
                value={linkedBid}
                onValueChange={(value) => {
                  setLinkedBid(value);
                  setFormData({...formData, bid_id: value});
                  setShowProjectFields(value === '' || value === 'none');
                }}
                disabled={loadingBids}
              >
                <SelectTrigger>
                  <SelectValue placeholder={loadingBids ? "Loading bids..." : "Select a bid"} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No bid linked</SelectItem>
                  {bids.filter(bid => bid.bid_id || bid.id).map(bid => (
                    <SelectItem key={bid.bid_id || bid.id} value={bid.bid_id || bid.id}>
                      {bid.project_name || bid.bid_number || 'Unnamed Bid'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {showProjectFields && (
            <>
              <div className="space-y-2">
                <Label htmlFor="project-name">Project Name</Label>
                <Input
                  id="project-name"
                  value={formData.project_name}
                  onChange={(e) => !isReadOnly && setFormData({...formData, project_name: e.target.value})}
                  readOnly={isReadOnly}
                  className={isReadOnly ? 'bg-muted' : ''}
                  placeholder="Enter project name"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="project-location">Project Location</Label>
                  <Input
                    id="project-location"
                    value={formData.project_location}
                    onChange={(e) => !isReadOnly && setFormData({...formData, project_location: e.target.value})}
                    readOnly={isReadOnly}
                    className={isReadOnly ? 'bg-muted' : ''}
                    placeholder="Enter location"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="client-name">Client Name</Label>
                  <Input
                    id="client-name"
                    value={formData.client_name}
                    onChange={(e) => !isReadOnly && setFormData({...formData, client_name: e.target.value})}
                    readOnly={isReadOnly}
                    className={isReadOnly ? 'bg-muted' : ''}
                    placeholder="Enter client name"
                  />
                </div>
              </div>
            </>
          )}

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => !isReadOnly && setFormData({...formData, description: e.target.value})}
              readOnly={isReadOnly}
              className={isReadOnly ? 'bg-muted' : ''}
              placeholder="Describe the scope and nature of this BOQ..."
              rows={3}
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="currency">Currency</Label>
              {isReadOnly ? (
                <Input
                  id="currency"
                  value={formData.currency}
                  readOnly={isReadOnly}
                  className="bg-muted"
                />
              ) : (
                <Select
                  value={formData.currency}
                  onValueChange={(value) => setFormData({...formData, currency: value})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CURRENCIES.map(c => <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="exchange-rate">Exchange Rate</Label>
              <Input
                id="exchange-rate"
                type="number"
                step="0.0001"
                value={formData.exchange_rate}
                onChange={(e) => !isReadOnly && setFormData({...formData, exchange_rate: parseFloat(e.target.value)})}
                readOnly={isReadOnly}
                className={isReadOnly ? 'bg-muted' : ''}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              {isReadOnly ? (
                <Input
                  id="status"
                  value={formData.status}
                  readOnly={isReadOnly}
                  className="bg-muted"
                />
              ) : (
                <Select
                  value={formData.status}
                  onValueChange={(value) => setFormData({...formData, status: value})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="under_review">Under Review</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                    <SelectItem value="final">Final</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="total-amount">Total Amount</Label>
              <Input
                id="total-amount"
                type="number"
                value={formData.total_amount}
                onChange={(e) => !isReadOnly && setFormData({...formData, total_amount: parseFloat(e.target.value)})}
                readOnly={isReadOnly}
                className={isReadOnly ? 'bg-muted' : ''}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="contingency-percentage">Contingency %</Label>
              <Input
                id="contingency-percentage"
                type="number"
                step="0.01"
                value={formData.contingency_percentage}
                onChange={(e) => !isReadOnly && setFormData({...formData, contingency_percentage: parseFloat(e.target.value)})}
                readOnly={isReadOnly}
                className={isReadOnly ? 'bg-muted' : ''}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="contingency-amount">Contingency Amount</Label>
              <Input
                id="contingency-amount"
                type="number"
                value={formData.contingency_amount}
                onChange={(e) => !isReadOnly && setFormData({...formData, contingency_amount: parseFloat(e.target.value)})}
                readOnly={isReadOnly}
                className={isReadOnly ? 'bg-muted' : ''}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="final-amount">Final Amount</Label>
              <Input
                id="final-amount"
                type="number"
                value={formData.final_amount}
                onChange={(e) => !isReadOnly && setFormData({...formData, final_amount: parseFloat(e.target.value)})}
                readOnly={isReadOnly}
                className={isReadOnly ? 'bg-muted' : ''}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="validity-period">Validity Period (Months)</Label>
              <Input
                id="validity-period"
                type="number"
                value={formData.validity_period}
                onChange={(e) => !isReadOnly && setFormData({...formData, validity_period: parseInt(e.target.value)})}
                readOnly={isReadOnly}
                className={isReadOnly ? 'bg-muted' : ''}
              />
            </div>
          </div>
          
          {isReadOnly && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="created-date">Created Date</Label>
                <Input
                  id="created-date"
                  defaultValue={boq?.createdDate}
                  readOnly={isReadOnly}
                  className="bg-muted"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="estimated-value">Estimated Value</Label>
                <Input
                  id="estimated-value"
                  value={`${formData.currency} ${formData.final_amount.toLocaleString()}`}
                  readOnly={isReadOnly}
                  className="bg-muted"
                />
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
          {mode === 'edit' && (
            <Button onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// BOQ Item Edit Modal
function BOQItemEditModal({ item, boqId, trigger, onSuccess }: { item: any; boqId?: string; trigger: React.ReactNode; onSuccess?: () => void }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showCostBreakdown, setShowCostBreakdown] = useState(
    !!(item?.labor_cost || item?.material_cost || item?.equipment_cost || item?.overhead_percentage || item?.profit_percentage)
  );
  const [formData, setFormData] = useState({
    item_number: item?.code || item?.item_number || '',
    description: item?.item || item?.description || '',
    specification: item?.specification || '',
    unit_of_measure: normalizeUnitValue(item?.unit || item?.unit_of_measure || 'each'),
    quantity: item?.quantity || 0,
    unit_rate: item?.rate || item?.unit_rate || 0,
    work_category: item?.category || item?.work_category || 'sitework',
    remarks: item?.remarks || '',
    drawing_reference: item?.drawing_reference || '',
    labor_cost: item?.labor_cost || 0,
    material_cost: item?.material_cost || 0,
    equipment_cost: item?.equipment_cost || 0,
    overhead_percentage: item?.overhead_percentage || 0,
    profit_percentage: item?.profit_percentage || 0,
    parent_item_id: item?.parent_item_id || '',
    level: item?.level || 1
  });

  const totalAmount = formData.quantity * formData.unit_rate;

  const generateUUID = () => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  };

  const handleSave = async () => {
    if (!formData.item_number || !formData.description || !formData.unit_of_measure) {
      showError({
        title: 'Validation Error',
        message: 'Please fill in required fields: Item Code, Description, and Unit of Measure'
      });
      return;
    }

    if (!boqId) {
      showError({
        title: 'No BOQ Selected',
        message: 'Please select a BOQ first.'
      });
      return;
    }

    setSaving(true);
    try {
      const payload: any = {
        item_number: formData.item_number,
        description: formData.description,
        specification: formData.specification || '',
        work_category: formData.work_category,
        quantity: formData.quantity,
        unit_of_measure: formData.unit_of_measure,
        unit_rate: formData.unit_rate,
        total_amount: totalAmount,
        labor_cost: formData.labor_cost,
        material_cost: formData.material_cost,
        equipment_cost: formData.equipment_cost,
        overhead_percentage: formData.overhead_percentage,
        profit_percentage: formData.profit_percentage,
        remarks: formData.remarks || '',
        drawing_reference: formData.drawing_reference || '',
        level: formData.level,
        boq_id: boqId
      };

      // Only include parent_item_id if it's provided
      if (formData.parent_item_id) {
        payload.parent_item_id = formData.parent_item_id;
      }

      if (import.meta.env.DEV) {
        console.log('=== Updating BOQ Item ===');
        console.log('BOQ ID:', boqId);
        console.log('Item ID:', item?.id);
        console.log('Full Payload:', JSON.stringify(payload, null, 2));
      }

      await httpClient.put(`/api/v1/boqs/items/${item?.id}`, payload);

      showSuccess({
        title: 'Item Updated',
        message: 'BOQ item updated successfully!'
      });

      setOpen(false);

      if (onSuccess) {
        onSuccess();
      }
    } catch (error: any) {
      console.error('Failed to update BOQ item:', error);
      showError({
        title: 'Update Failed',
        message: error.message || 'Failed to update BOQ item. Please try again.'
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto modal-content">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Edit className="h-5 w-5" />
            Edit BOQ Item
          </DialogTitle>
          <DialogDescription>
            Update the BOQ item details
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="edit-item-code">Item Code</Label>
            <Input
              id="edit-item-code"
              placeholder="e.g., EXC-001"
              value={formData.item_number}
              onChange={(e) => setFormData({...formData, item_number: e.target.value})}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-item-description">Item Description</Label>
            <Textarea
              id="edit-item-description"
              placeholder="Detailed description of the work item..."
              rows={2}
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="edit-unit">Unit</Label>
              <Select value={formData.unit_of_measure} onValueChange={(value) => setFormData({...formData, unit_of_measure: value})}>
                <SelectTrigger>
                  <SelectValue placeholder="Select unit" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="each">Each</SelectItem>
                  <SelectItem value="lf">Linear Meter</SelectItem>
                  <SelectItem value="sf">Square Meter</SelectItem>
                  <SelectItem value="cf">Cubic Meter</SelectItem>
                  <SelectItem value="sy">Square Meter</SelectItem>
                  <SelectItem value="cy">Cubic Meter</SelectItem>
                  <SelectItem value="lbs">Kilogram</SelectItem>
                  <SelectItem value="tons">Tons</SelectItem>
                  <SelectItem value="hours">Hours</SelectItem>
                  <SelectItem value="days">Days</SelectItem>
                  <SelectItem value="ls">Lump Sum</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-quantity">Quantity</Label>
              <Input
                id="edit-quantity"
                type="number"
                placeholder="0"
                value={formData.quantity}
                onChange={(e) => setFormData({...formData, quantity: parseFloat(e.target.value) || 0})}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-rate">Unit Rate</Label>
              <Input
                id="edit-rate"
                type="number"
                placeholder="0.00"
                step="0.01"
                value={formData.unit_rate}
                onChange={(e) => setFormData({...formData, unit_rate: parseFloat(e.target.value) || 0})}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-total-amount">Total Amount</Label>
            <Input
              id="edit-total-amount"
              type="number"
              value={totalAmount.toFixed(2)}
              readOnly
              className="bg-muted"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-category">Category</Label>
            <Select value={formData.work_category} onValueChange={(value) => setFormData({...formData, work_category: value})}>
              <SelectTrigger>
                <SelectValue placeholder="Select category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="earthwork">Earthwork & Excavation</SelectItem>
                <SelectItem value="concrete">Concrete Work</SelectItem>
                <SelectItem value="masonry">Masonry</SelectItem>
                <SelectItem value="steel">Steel & Reinforcement</SelectItem>
                <SelectItem value="roofing">Roofing</SelectItem>
                <SelectItem value="electrical">Electrical</SelectItem>
                <SelectItem value="plumbing">Plumbing</SelectItem>
                <SelectItem value="finishing">Finishing Work</SelectItem>
                <SelectItem value="misc">Miscellaneous</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="edit-cost-breakdown"
              checked={showCostBreakdown}
              onChange={(e) => setShowCostBreakdown(e.target.checked)}
              className="h-4 w-4"
            />
            <Label htmlFor="edit-cost-breakdown" className="cursor-pointer">
              Add Cost Breakdown Components
            </Label>
          </div>

          {showCostBreakdown && (
            <div className="space-y-4 border-t pt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-labor-cost">Labor Cost</Label>
                  <Input
                    id="edit-labor-cost"
                    type="number"
                    step="0.01"
                    value={formData.labor_cost}
                    onChange={(e) => setFormData({...formData, labor_cost: parseFloat(e.target.value) || 0})}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-material-cost">Material Cost</Label>
                  <Input
                    id="edit-material-cost"
                    type="number"
                    step="0.01"
                    value={formData.material_cost}
                    onChange={(e) => setFormData({...formData, material_cost: parseFloat(e.target.value) || 0})}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-equipment-cost">Equipment Cost</Label>
                  <Input
                    id="edit-equipment-cost"
                    type="number"
                    step="0.01"
                    value={formData.equipment_cost}
                    onChange={(e) => setFormData({...formData, equipment_cost: parseFloat(e.target.value) || 0})}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-overhead-percentage">Overhead %</Label>
                  <Input
                    id="edit-overhead-percentage"
                    type="number"
                    step="0.01"
                    value={formData.overhead_percentage}
                    onChange={(e) => setFormData({...formData, overhead_percentage: parseFloat(e.target.value) || 0})}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-profit-percentage">Profit %</Label>
                <Input
                  id="edit-profit-percentage"
                  type="number"
                  step="0.01"
                  value={formData.profit_percentage}
                  onChange={(e) => setFormData({...formData, profit_percentage: parseFloat(e.target.value) || 0})}
                />
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="edit-notes">Notes (Optional)</Label>
            <Textarea
              id="edit-notes"
              placeholder="Additional notes or specifications..."
              rows={2}
              value={formData.remarks}
              onChange={(e) => setFormData({...formData, remarks: e.target.value})}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// BOQ Item Delete Modal
function BOQItemDeleteModal({ item, boqId, trigger, onSuccess }: { item: any; boqId?: string; trigger: React.ReactNode; onSuccess?: () => void }) {
  const [open, setOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    if (!boqId) {
      showError({
        title: 'No BOQ Selected',
        message: 'Please select a BOQ first.'
      });
      return;
    }

    setDeleting(true);
    try {
      if (import.meta.env.DEV) {
        console.log('=== Deleting BOQ Item ===');
        console.log('BOQ ID:', boqId);
        console.log('Item ID:', item?.id);
      }

      await httpClient.delete(`/api/v1/boqs/items/${item?.id}`);

      showSuccess({
        title: 'Item Deleted',
        message: 'BOQ item deleted successfully!'
      });

      setOpen(false);

      if (onSuccess) {
        onSuccess();
      }
    } catch (error: any) {
      console.error('Failed to delete BOQ item:', error);
      showError({
        title: 'Delete Failed',
        message: error.message || 'Failed to delete BOQ item. Please try again.'
      });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px] modal-content">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="h-5 w-5" />
            Delete BOQ Item
          </DialogTitle>
          <DialogDescription>
            This action cannot be undone. This will permanently delete the BOQ item.
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-destructive mt-0.5" />
              <div>
                <h4 className="font-medium text-destructive">Confirm Deletion</h4>
                <p className="text-sm text-destructive/80 mt-1">
                  You are about to delete <strong>{item?.code}: {item?.item}</strong>
                </p>
                <p className="text-sm text-destructive/80 mt-1">
                  This will affect the total BOQ value calculation.
                </p>
              </div>
            </div>
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} disabled={deleting}>Cancel</Button>
          <Button variant="destructive" onClick={handleDelete} disabled={deleting}>
            <Trash2 className="h-4 w-4 mr-2" />
            {deleting ? 'Deleting...' : 'Delete Item'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EstimateItemEditModal({ item, estimateId, trigger, onSuccess }: { item: any; estimateId: string; trigger: React.ReactNode; onSuccess?: () => void }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    item_code: item?.code || item?.item_code || '',
    description: item?.description || '',
    work_category: item?.category || item?.work_category || '',
    cost_type: item?.costType || item?.cost_type || '',
    quantity: item?.quantity || 0,
    unit_of_measure: item?.unit || item?.unit_of_measure || '',
    unit_rate: item?.rate || item?.unit_rate || 0,
    notes: item?.notes || '',
  });

  const totalCost = formData.quantity * formData.unit_rate;

  const handleSave = async () => {
    if (!formData.description || !formData.unit_of_measure || !formData.quantity || !formData.cost_type) {
      showError({ title: 'Validation Error', message: 'Description, Unit, Quantity, and Cost Type are required.' });
      return;
    }
    setSaving(true);
    try {
      await httpClient.patch(`/api/v1/estimates/${estimateId}/items/${item?.id}`, {
        ...formData,
        total_cost: totalCost,
      });
      showSuccess({ title: 'Item Updated', message: 'Estimate item updated successfully!' });
      setOpen(false);
      onSuccess?.();
    } catch (error: any) {
      showError({ title: 'Update Failed', message: error.message || 'Failed to update estimate item.' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="sm:max-w-[600px] modal-content">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Edit className="h-5 w-5" />Edit Estimate Item</DialogTitle>
          <DialogDescription>Update this line item</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Item Code</Label>
              <Input value={formData.item_code} onChange={(e) => setFormData({...formData, item_code: e.target.value})} />
            </div>
            <div className="space-y-2">
              <Label>Work Category</Label>
              <Select value={formData.work_category} onValueChange={(v) => setFormData({...formData, work_category: v})}>
                <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="earthwork">Earthwork & Excavation</SelectItem>
                  <SelectItem value="concrete">Concrete Work</SelectItem>
                  <SelectItem value="masonry">Masonry</SelectItem>
                  <SelectItem value="steel">Steel & Reinforcement</SelectItem>
                  <SelectItem value="roofing">Roofing</SelectItem>
                  <SelectItem value="electrical">Electrical</SelectItem>
                  <SelectItem value="plumbing">Plumbing</SelectItem>
                  <SelectItem value="finishing">Finishing Work</SelectItem>
                  <SelectItem value="misc">Miscellaneous</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Description <span className="text-destructive">*</span></Label>
            <Textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} rows={2} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Cost Type <span className="text-destructive">*</span></Label>
              <Select value={formData.cost_type} onValueChange={(v) => setFormData({...formData, cost_type: v})}>
                <SelectTrigger><SelectValue placeholder="Select cost type" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="labor">Labor</SelectItem>
                  <SelectItem value="material">Material</SelectItem>
                  <SelectItem value="equipment">Equipment</SelectItem>
                  <SelectItem value="subcontractor">Subcontractor</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Unit of Measure <span className="text-destructive">*</span></Label>
              <Select value={formData.unit_of_measure} onValueChange={(v) => setFormData({...formData, unit_of_measure: v})}>
                <SelectTrigger><SelectValue placeholder="Select unit" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="m³">m³ - Cubic Meter</SelectItem>
                  <SelectItem value="m²">m² - Square Meter</SelectItem>
                  <SelectItem value="m">m - Linear Meter</SelectItem>
                  <SelectItem value="kg">kg - Kilogram</SelectItem>
                  <SelectItem value="ton">ton - Metric Ton</SelectItem>
                  <SelectItem value="each">each - Each</SelectItem>
                  <SelectItem value="hr">hr - Hour</SelectItem>
                  <SelectItem value="LS">LS - Lump Sum</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Quantity <span className="text-destructive">*</span></Label>
              <Input type="number" step="0.0001" value={formData.quantity} onChange={(e) => setFormData({...formData, quantity: parseFloat(e.target.value) || 0})} />
            </div>
            <div className="space-y-2">
              <Label>Unit Rate</Label>
              <Input type="number" step="0.01" value={formData.unit_rate} onChange={(e) => setFormData({...formData, unit_rate: parseFloat(e.target.value) || 0})} />
            </div>
            <div className="space-y-2">
              <Label>Total Cost</Label>
              <Input value={totalCost.toFixed(2)} readOnly className="bg-muted" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save Changes'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EstimateItemDeleteModal({ item, estimateId, trigger, onSuccess }: { item: any; estimateId: string; trigger: React.ReactNode; onSuccess?: () => void }) {
  const [open, setOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await httpClient.delete(`/api/v1/estimates/${estimateId}/items/${item?.id}`);
      showSuccess({ title: 'Item Deleted', message: 'Estimate item deleted successfully!' });
      setOpen(false);
      onSuccess?.();
    } catch (error: any) {
      showError({ title: 'Delete Failed', message: error.message || 'Failed to delete estimate item.' });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="sm:max-w-[450px] modal-content">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="h-5 w-5" />Delete Estimate Item
          </DialogTitle>
          <DialogDescription>This action cannot be undone.</DialogDescription>
        </DialogHeader>
        <div className="py-4">
          <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
            <p className="text-sm text-destructive/80">
              You are about to delete <strong>{item?.code || item?.item_code}: {item?.description}</strong>.
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} disabled={deleting}>Cancel</Button>
          <Button variant="destructive" onClick={handleDelete} disabled={deleting}>
            <Trash2 className="h-4 w-4 mr-2" />{deleting ? 'Deleting...' : 'Delete Item'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Approval Process Modal (Generic for BOQ and Estimates)
function ApprovalProcessModal({ type, item, trigger, onSuccess }: { type: 'BOQ' | 'Estimate' | 'Bid'; item: any; trigger: React.ReactNode; onSuccess?: () => void }) {
  const [decision, setDecision] = useState<'approve' | 'reject' | ''>('');
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  // Bid Analysis specific state
  const [bidAnalysis, setBidAnalysis] = useState({
    win_probability: 0,
    competitor_analysis: '',
    risk_assessment: ''
  });

  const isBidType = type === 'Bid';

  const handleApprovalDecision = async () => {
    setSaving(true);
    try {
      if (type === 'BOQ') {
        const newStatus = decision === 'approve' ? 'approved' : 'rejected';
        await httpClient.post(`/api/v1/boqs/${item?.id}/status`, { status: newStatus });

        showSuccess({
          title: decision === 'approve' ? 'BOQ Approved' : 'BOQ Rejected',
          message: `BOQ has been ${decision === 'approve' ? 'approved' : 'rejected'} successfully!`
        });
      } else if (type === 'Estimate') {
        const endpoint = decision === 'approve'
          ? `/api/v1/estimates/${item?.id}/approve`
          : `/api/v1/estimates/${item?.id}/reject`;

        await httpClient.post(endpoint, {});

        showSuccess({
          title: decision === 'approve' ? 'Estimate Approved' : 'Estimate Rejected',
          message: `Estimate has been ${decision === 'approve' ? 'approved' : 'rejected'} successfully!`
        });
      }

      setOpen(false);
      setDecision('');

      // Trigger data refresh
      if (onSuccess) {
        onSuccess();
      }
    } catch (error: any) {
      console.error('Failed to process approval:', error);
      showError({
        title: 'Approval Failed',
        message: error.message || 'Failed to process approval. Please try again.'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveBidAnalysis = async () => {
    setSaving(true);
    try {
      const userProfile = await authService.getUserProfile();
      const organizationId = userProfile.organizationId;

      const payload = {
        win_probability: bidAnalysis.win_probability,
        competitor_analysis: bidAnalysis.competitor_analysis,
        risk_assessment: bidAnalysis.risk_assessment
      };

      if (import.meta.env.DEV) {
        console.log('Bid Analysis Payload:', {
          bid_id: item?.id,
          endpoint: `/api/v1/bids/${item?.id}/analysis`,
          payload: payload
        });
      }

      await httpClient.post(
        `/api/v1/bids/${item?.id}/analysis?organization_id=${organizationId}`,
        payload
      );

      showSuccess({
        title: 'Analysis Saved',
        message: 'Bid analysis saved successfully!'
      });

      setOpen(false);

      // Trigger data refresh
      if (onSuccess) {
        onSuccess();
      }
    } catch (error: any) {
      console.error('Failed to save bid analysis:', error);
      showError({
        title: 'Save Failed',
        message: error.message || 'Failed to save bid analysis. Please try again.'
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px] modal-content">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-green-600" />
            {isBidType ? 'Bid Analysis' : `${type} Approval Process`}
          </DialogTitle>
          <DialogDescription>
            {isBidType ? 'Analyze bid opportunity and assess win probability' : `Review and approve or reject this ${type.toLowerCase()}`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="p-4 bg-muted/50 rounded-lg">
            <h4 className="font-medium">{type} Details</h4>
            <p className="text-sm text-muted-foreground mt-1">
              {type}: {item?.name || item?.project}
            </p>
            {item?.value && (
              <p className="text-sm text-muted-foreground">
                Value: {item.value}
              </p>
            )}
            <p className="text-sm text-muted-foreground">
              Status: {item?.status}
            </p>
          </div>

          {isBidType ? (
            // Bid Analysis Fields
            <>
              <div className="space-y-3">
                <Label>Win Probability</Label>
                <div className="grid grid-cols-2 gap-3">
                  <Button
                    variant={bidAnalysis.win_probability === 1 ? 'default' : 'outline'}
                    className={bidAnalysis.win_probability === 1 ? 'bg-green-600 hover:bg-green-700' : ''}
                    onClick={() => setBidAnalysis({...bidAnalysis, win_probability: 1})}
                  >
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Yes
                  </Button>
                  <Button
                    variant={bidAnalysis.win_probability === 0 ? 'destructive' : 'outline'}
                    onClick={() => setBidAnalysis({...bidAnalysis, win_probability: 0})}
                  >
                    <XCircle className="h-4 w-4 mr-2" />
                    No
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="competitor-analysis">Competitor Analysis</Label>
                <Textarea
                  id="competitor-analysis"
                  placeholder="Analyze competitor landscape, strengths, and weaknesses..."
                  rows={3}
                  value={bidAnalysis.competitor_analysis}
                  onChange={(e) => setBidAnalysis({...bidAnalysis, competitor_analysis: e.target.value})}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="risk-assessment">Risk Assessment</Label>
                <Textarea
                  id="risk-assessment"
                  placeholder="Identify and assess potential risks..."
                  rows={3}
                  value={bidAnalysis.risk_assessment}
                  onChange={(e) => setBidAnalysis({...bidAnalysis, risk_assessment: e.target.value})}
                />
              </div>

              {bidAnalysis.win_probability === 1 && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600 mt-0.5" />
                    <div className="text-sm">
                      <p className="font-medium text-green-900">High Win Probability</p>
                      <p className="text-green-700">
                        This bid shows strong potential for success. Proceed with detailed preparation.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {bidAnalysis.win_probability === 0 && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <XCircle className="h-4 w-4 text-red-600 mt-0.5" />
                    <div className="text-sm">
                      <p className="font-medium text-red-900">Low Win Probability</p>
                      <p className="text-red-700">
                        This bid may not be worth pursuing. Consider focusing resources elsewhere.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            // Original Approval/Reject Flow for BOQ and Estimates
            <>
              <div className="space-y-3">
                <Label>Decision</Label>
                <div className="grid grid-cols-2 gap-3">
                  <Button
                    variant={decision === 'approve' ? 'default' : 'outline'}
                    className={decision === 'approve' ? 'bg-green-600 hover:bg-green-700' : ''}
                    onClick={() => setDecision('approve')}
                  >
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Approve
                  </Button>
                  <Button
                    variant={decision === 'reject' ? 'destructive' : 'outline'}
                    onClick={() => setDecision('reject')}
                  >
                    <XCircle className="h-4 w-4 mr-2" />
                    Reject
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="approval-reason">
                  {decision === 'approve' ? 'Approval' : decision === 'reject' ? 'Rejection' : ''} Reason
                </Label>
                <Textarea
                  id="approval-reason"
                  placeholder={
                    decision === 'approve'
                      ? "Optional: Add approval comments..."
                      : decision === 'reject'
                      ? "Please provide reason for rejection..."
                      : "Select approve or reject first..."
                  }
                  rows={3}
                  disabled={!decision}
                />
              </div>

              {decision === 'approve' && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600 mt-0.5" />
                    <div className="text-sm">
                      <p className="font-medium text-green-900">Approval Action</p>
                      <p className="text-green-700">
                        This {type.toLowerCase()} will be marked as approved and made available for next phase.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {decision === 'reject' && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <XCircle className="h-4 w-4 text-red-600 mt-0.5" />
                    <div className="text-sm">
                      <p className="font-medium text-red-900">Rejection Action</p>
                      <p className="text-red-700">
                        This {type.toLowerCase()} will be sent back for revision with your feedback.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            disabled={isBidType ? saving : !decision || saving}
            onClick={isBidType ? handleSaveBidAnalysis : handleApprovalDecision}
          >
            {saving
              ? 'Saving...'
              : isBidType
                ? 'Submit Analysis'
                : `Submit ${decision === 'approve' ? 'Approval' : decision === 'reject' ? 'Rejection' : 'Decision'}`
            }
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function BOQManagement() {
  const { canWrite, canApprove, canDelete } = usePermission();
  const orgCurrency = useOrgCurrency();
  const [activeTab, setActiveTab] = useState("details");
  const [selectedBOQ, setSelectedBOQ] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const { orgId } = useAuth();
  const { data: rawBoqs = [], isLoading: loading, error: boqsError, refetch: refetchBoqs } = useBoqs(orgId);
  const { data: rawBids = [] } = useBids(orgId);
  const { data: rawBoqItems = [], isLoading: loadingItems, refetch: refetchBoqItems } = useBoqItems(selectedBOQ);
  const error = boqsError ? (boqsError as Error).message || 'Failed to load BOQs' : null;

  const refreshBOQs = () => { refetchBoqs(); };
  const refreshBOQItems = () => { refetchBoqItems(); };

  const bidsMap: Record<string, string> = useMemo(() =>
    (rawBids as any[]).reduce((acc: Record<string, string>, bid: any) => {
      acc[bid.bid_id] = bid.project_name || bid.bid_number || 'Unknown Bid';
      return acc;
    }, {}),
    [rawBids]
  );

  const boqList: any[] = useMemo(() => (rawBoqs as any[]).map((boq: any) => {
    let formattedDate = new Date().toISOString().split('T')[0];
    if (boq.created_at) {
      const match = boq.created_at.match(/^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2})/);
      formattedDate = match ? match[1] : boq.created_at.split('.')[0];
    } else if (boq.preparation_date) {
      formattedDate = boq.preparation_date;
    }
    return {
      id: boq.boq_id || boq.id,
      name: boq.boq_name || boq.name,
      bid: boq.bid_id ? (bidsMap[boq.bid_id] || boq.bid_number || 'No bid linked') : 'No bid linked',
      bid_id: boq.bid_id,
      status: boq.status || 'draft',
      createdDate: formattedDate,
      value: formatCurrencyCompact(parseFloat(boq.final_amount) || 0, orgCurrency),
      ...boq
    };
  }), [rawBoqs, bidsMap]);

  const boqItems: any[] = useMemo(() => (rawBoqItems as any[]).map((item: any) => ({
    id: item.boq_item_id || item.id,
    code: item.item_number,
    item: item.description,
    unit: item.unit_of_measure,
    quantity: parseFloat(item.quantity) || 0,
    rate: parseFloat(item.unit_rate) || 0,
    amount: parseFloat(item.total_amount) || 0,
    category: item.work_category,
    ...item
  })), [rawBoqItems]);

  // Auto-select first valid BOQ when list loads
  useEffect(() => {
    if (boqList.length > 0 && !selectedBOQ) {
      const firstValidBOQ = boqList.find((boq: any) =>
        /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(boq.boq_id)
      );
      if (firstValidBOQ) setSelectedBOQ(firstValidBOQ.boq_id);
    }
  }, [boqList, selectedBOQ]);

  // Update BOQ status
  const updateBOQStatus = async (boqId: string, newStatus: string) => {
    try {
      if (import.meta.env.DEV) {
        console.log('Updating BOQ status:', boqId, 'to', newStatus);
      }

      await httpClient.post(`/api/v1/boqs/${boqId}/status`, { status: newStatus });

      if (import.meta.env.DEV) {
        console.log('BOQ status updated successfully');
      }
    } catch (error: any) {
      console.error('Failed to update BOQ status:', error);
      throw error;
    }
  };

  // Recalculate BOQ totals
  const handleRecalculate = async (boqId: string) => {
    try {
      if (import.meta.env.DEV) {
        console.log('Recalculating BOQ:', boqId);
      }

      await httpClient.post(`/api/v1/boqs/${boqId}/recalculate`);

      // Change status to Under Review after recalculation
      await updateBOQStatus(boqId, 'under_review');

      showSuccess({
        title: 'Recalculated',
        message: 'BOQ totals have been recalculated and status updated to Under Review!'
      });

      refreshBOQs();
    } catch (error: any) {
      console.error('Failed to recalculate BOQ:', error);
      showError({
        title: 'Recalculation Failed',
        message: error.message || 'Failed to recalculate BOQ. Please try again.'
      });
    }
  };


  const filteredBOQs = boqList.filter(boq => {
    const matchesSearch = boq.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         boq.bid.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === "all" || boq.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const {
    currentPage: boqCurrentPage,
    totalPages: boqTotalPages,
    itemsPerPage: boqItemsPerPage,
    paginatedData: paginatedBOQs,
    setCurrentPage: setBoqCurrentPage,
    setItemsPerPage: setBoqItemsPerPage
  } = usePagination({ data: filteredBOQs, initialItemsPerPage: 10 });

  const {
    currentPage: boqItemsCurrentPage,
    totalPages: boqItemsTotalPages,
    itemsPerPage: boqItemsItemsPerPage,
    paginatedData: paginatedBOQItems,
    setCurrentPage: setBoqItemsCurrentPage,
    setItemsPerPage: setBoqItemsItemsPerPage
  } = usePagination({ data: boqItems, initialItemsPerPage: 10 });

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "draft": return <Badge variant="outline">Draft</Badge>;
      case "in_progress": return <Badge className="bg-blue-100 text-blue-800">In Progress</Badge>;
      case "under_review": return <Badge className="bg-yellow-100 text-yellow-800">Under Review</Badge>;
      case "approved": return <Badge variant="default">Approved</Badge>;
      case "rejected": return <Badge variant="destructive">Rejected</Badge>;
      case "final": return <Badge className="bg-green-600 text-white">Final</Badge>;
      default: return <Badge variant="outline">{status}</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">BOQ Management</h2>
          <p className="text-muted-foreground">Bills of Quantities creation, management, and optimization</p>
        </div>
        <div className="flex gap-2">
          {canWrite('/boq') && <ImportBOQModal />}
          {canWrite('/boq') && <NewBOQModal onSuccess={refreshBOQs} />}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Calculator className="h-4 w-4 text-blue-600" />
              Active BOQs
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-700">{boqList.length}</div>
            <p className="text-xs text-blue-600 mt-1">Across all projects</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-green-600" />
              Total Estimated Value
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-700">
              {(() => {
                const total = boqList.reduce((sum, boq) => {
                  return sum + (parseFloat(boq.final_amount) || parseFloat(boq.total_amount) || 0);
                }, 0);
                return formatCurrencyCompact(total, orgCurrency);
              })()}
            </div>
            <p className="text-xs text-green-600 mt-1">All active BOQs</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-emerald-600" />
              Finalized BOQs
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-emerald-700">{boqList.filter((b: any) => b.status === 'final' || b.status === 'approved').length}</div>
            <p className="text-xs text-emerald-600 mt-1">Ready for tendering</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calculator className="h-5 w-5" />
            BOQ Management
          </CardTitle>
          <CardDescription>Manage your Bills of Quantities and items</CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="details">BOQ Details</TabsTrigger>
              <TabsTrigger value="items">BOQ Items</TabsTrigger>
            </TabsList>
            
            <TabsContent value="details" className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input 
                    placeholder="Search BOQs..." 
                    className="pl-9"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="under_review">Under Review</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                    <SelectItem value="final">Final</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>BOQ Name</TableHead>
                    <TableHead>Linked Bid</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created Date</TableHead>
                    <TableHead className="text-right">Est. Value</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedBOQs.map((boq) => (
                    <TableRow key={boq.id}>
                      <TableCell className="font-medium">{boq.name}</TableCell>
                      <TableCell>
                        {boq.bid !== "No bid linked" ? (
                          <div className="flex items-center gap-1">
                            <Link className="h-3 w-3" />
                            {boq.bid}
                          </div>
                        ) : (
                          <span className="text-muted-foreground">No bid linked</span>
                        )}
                      </TableCell>
                      <TableCell>{getStatusBadge(boq.status)}</TableCell>
                      <TableCell className="text-muted-foreground">{boq.createdDate}</TableCell>
                      <TableCell className="text-right font-medium">{boq.value}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <BOQDetailsModal
                            mode="view"
                            boq={boq}
                            trigger={
                              <Button variant="ghost" size="sm" title="View BOQ">
                                <Eye className="h-4 w-4" />
                              </Button>
                            }
                            onSuccess={refreshBOQs}
                          />
                          {canWrite('/boq') && boq.status !== 'approved' && boq.status !== 'final' && (
                            <BOQDetailsModal
                              mode="edit"
                              boq={boq}
                              trigger={
                                <Button variant="ghost" size="sm" title="Edit BOQ">
                                  <Edit className="h-4 w-4" />
                                </Button>
                              }
                              onSuccess={refreshBOQs}
                            />
                          )}
                          {boq.status !== 'approved' && boq.status !== 'final' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              title="Recalculate Totals"
                              onClick={() => handleRecalculate(boq.id)}
                            >
                              <Calculator className="h-4 w-4" />
                            </Button>
                          )}
                          {canApprove('/boq') && boq.status !== 'approved' && boq.status !== 'final' && (
                            <ApprovalProcessModal
                              type="BOQ"
                              item={boq}
                              trigger={
                                <Button variant="ghost" size="sm" title="Process Approval">
                                  <CheckCircle className="h-4 w-4" />
                                </Button>
                              }
                              onSuccess={refreshBOQs}
                            />
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <Pagination
                currentPage={boqCurrentPage}
                totalPages={boqTotalPages}
                totalItems={filteredBOQs.length}
                itemsPerPage={boqItemsPerPage}
                onPageChange={setBoqCurrentPage}
                onItemsPerPageChange={setBoqItemsPerPage}
              />
            </TabsContent>

            <TabsContent value="items" className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="space-y-2">
                  <Label>Select BOQ</Label>
                  <Select value={selectedBOQ} onValueChange={setSelectedBOQ}>
                    <SelectTrigger className="w-64">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {boqList.filter(boq => boq.id).map((boq) => (
                        <SelectItem key={boq.id} value={boq.id}>{boq.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex-1" />
                {canWrite('/boq') && <AddBOQItemModal boqId={selectedBOQ} onSuccess={refreshBOQItems} />}
              </div>
              
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Code</TableHead>
                    <TableHead>Item Description</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Unit</TableHead>
                    <TableHead className="text-right">Quantity</TableHead>
                    <TableHead className="text-right">Rate</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedBOQItems.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell className="font-mono text-sm">{item.code}</TableCell>
                      <TableCell className="font-medium">{item.item}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">{item.category}</Badge>
                      </TableCell>
                      <TableCell>{getReadableUnit(item.unit)}</TableCell>
                      <TableCell className="text-right">{item.quantity.toLocaleString()}</TableCell>
                      <TableCell className="text-right">{formatCurrency(item.rate || 0, orgCurrency)}</TableCell>
                      <TableCell className="text-right font-medium">{formatCurrency(item.amount || 0, orgCurrency)}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {canWrite('/boq') && (
                            <BOQItemEditModal
                              item={item}
                              boqId={selectedBOQ}
                              onSuccess={refreshBOQItems}
                              trigger={
                                <Button variant="ghost" size="sm" title="Edit Item">
                                  <Edit className="h-4 w-4" />
                                </Button>
                              }
                            />
                          )}
                          {canDelete('/boq') && (
                            <BOQItemDeleteModal
                              item={item}
                              boqId={selectedBOQ}
                              onSuccess={refreshBOQItems}
                              trigger={
                                <Button variant="ghost" size="sm" title="Delete Item" className="text-destructive hover:text-destructive">
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              }
                            />
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <Pagination
                currentPage={boqItemsCurrentPage}
                totalPages={boqItemsTotalPages}
                totalItems={boqItems.length}
                itemsPerPage={boqItemsItemsPerPage}
                onPageChange={setBoqItemsCurrentPage}
                onItemsPerPageChange={setBoqItemsItemsPerPage}
              />

              <div className="flex justify-between items-center pt-4 border-t">
                <div className="text-sm text-muted-foreground">
                  Total: {boqItems.length} items
                </div>
                <div className="text-xl font-bold">
                  Total Amount: {formatCurrency(boqItems.reduce((sum, item) => sum + item.amount, 0), orgCurrency)}
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}

// New Estimate Modal Component
function NewEstimateModal({ onSuccess }: { onSuccess?: () => void }) {
  const orgCurrency = useOrgCurrency();
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedBoq, setSelectedBoq] = useState('');
  const [availableBOQs, setAvailableBOQs] = useState<any[]>([]);
  const [formData, setFormData] = useState({
    estimate_number: '',
    estimate_name: '',
    description: '',
    version: '1.0',
    boq_id: '',
    estimate_date: new Date().toISOString().split('T')[0],
    base_date: '',
    escalation_rate: 0,
    location_factor: 1.0,
    direct_costs: 0,
    indirect_costs: 0,
    overhead_percentage: 0,
    overhead_amount: 0,
    profit_percentage: 0,
    profit_amount: 0,
    contingency_percentage: 0,
    contingency_amount: 0,
    total_estimate: 0,
    accuracy_level: '',
    confidence_level: 0,
    currency: orgCurrency,
    status: 'draft'
  });

  // Fetch BOQs when modal opens
  useEffect(() => {
    const fetchBOQs = async () => {
      if (open) {
        try {
          const userProfile = await authService.getUserProfile();
          const organizationId = userProfile.organizationId;
          const response = await httpClient.get(`/api/v1/boqs/?organization_id=${organizationId}`);

          // Handle response format - could be array or object with data property
          let boqList = [];
          if (Array.isArray(response.data)) {
            boqList = response.data;
          } else if (response.data?.data && Array.isArray(response.data.data)) {
            boqList = response.data.data;
          } else if (response.data) {
            boqList = [response.data];
          }

          setAvailableBOQs(boqList);
        } catch (error) {
          console.error('Failed to fetch BOQs:', error);
          setAvailableBOQs([]);
        }
      }
    };
    fetchBOQs();
  }, [open]);

  const handleSave = async () => {
    if (!formData.estimate_number || !formData.estimate_name || !formData.boq_id) {
      showError({
        title: 'Validation Error',
        message: 'Please fill in required fields: Estimate Number, Estimate Name, and BOQ'
      });
      return;
    }

    setSaving(true);
    try {
      const userProfile = await authService.getUserProfile();
      const organizationId = userProfile.organizationId;

      const payload = {
        ...formData,
        boq_id: formData.boq_id || selectedBoq
      };

      if (import.meta.env.DEV) {
        console.log('=== Creating New Estimate ===');
        console.log('Organization ID:', organizationId);
        console.log('Full Payload:', JSON.stringify(payload, null, 2));
      }

      await httpClient.post(`/api/v1/estimates?organization_id=${organizationId}`, payload);

      showSuccess({
        title: 'Estimate Created',
        message: 'Estimate created successfully!'
      });

      setOpen(false);

      // Reset form
      setFormData({
        estimate_number: '',
        estimate_name: '',
        description: '',
        version: '1.0',
        boq_id: '',
        estimate_date: new Date().toISOString().split('T')[0],
        base_date: '',
        escalation_rate: 0,
        location_factor: 1.0,
        direct_costs: 0,
        indirect_costs: 0,
        overhead_percentage: 0,
        overhead_amount: 0,
        profit_percentage: 0,
        profit_amount: 0,
        contingency_percentage: 0,
        contingency_amount: 0,
        total_estimate: 0,
        accuracy_level: '',
        confidence_level: 0,
        currency: orgCurrency,
        status: 'draft'
      });
      setSelectedBoq('');

      // Call refresh callback instead of reloading page
      if (onSuccess) {
        onSuccess();
      }
    } catch (error: any) {
      console.error('Failed to save estimate:', error);
      showError({
        title: 'Creation Failed',
        message: error.message || 'Failed to save estimate. Please try again.'
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>
          <Target className="h-4 w-4 mr-2" />
          New Estimate
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto modal-content">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Target className="h-5 w-5" />
            Create New Estimate
          </DialogTitle>
          <DialogDescription>
            Create a new cost estimate linked to a BOQ
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Basic Information */}
          <div className="space-y-4">
            <h4 className="font-medium">Basic Information</h4>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="estimate-number">Estimate Number</Label>
                <Input
                  id="estimate-number"
                  placeholder="e.g., EST-2024-001"
                  value={formData.estimate_number}
                  onChange={(e) => setFormData({...formData, estimate_number: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="estimate-name">Estimate Name</Label>
                <Input
                  id="estimate-name"
                  placeholder="e.g., Hospital Wing Preliminary Estimate"
                  value={formData.estimate_name}
                  onChange={(e) => setFormData({...formData, estimate_name: e.target.value})}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="version">Version</Label>
                <Input
                  id="version"
                  placeholder="1.0"
                  value={formData.version}
                  onChange={(e) => setFormData({...formData, version: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="linked-boq">Link to BOQ *</Label>
                <Select value={selectedBoq} onValueChange={(value) => {
                  setSelectedBoq(value);
                  setFormData({...formData, boq_id: value});
                }}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a BOQ" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableBOQs.filter(boq => boq.boq_id || boq.id).map((boq) => (
                      <SelectItem key={boq.boq_id || boq.id} value={boq.boq_id || boq.id}>
                        {boq.boq_name || boq.name || 'Unnamed BOQ'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="estimate-description">Description</Label>
              <Textarea
                id="estimate-description"
                placeholder="Describe the estimate scope and assumptions..."
                rows={3}
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
              />
            </div>
          </div>

          {/* Estimate Parameters */}
          <div className="space-y-4">
            <h4 className="font-medium">Estimate Parameters</h4>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="estimate-date">Estimate Date</Label>
                <Input
                  id="estimate-date"
                  type="date"
                  value={formData.estimate_date}
                  onChange={(e) => setFormData({...formData, estimate_date: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="base-date">Base Date</Label>
                <Input
                  id="base-date"
                  type="date"
                  value={formData.base_date}
                  onChange={(e) => setFormData({...formData, base_date: e.target.value})}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="escalation-rate">Escalation Rate (%)</Label>
                <Input
                  id="escalation-rate"
                  type="number"
                  step="0.01"
                  value={formData.escalation_rate}
                  onChange={(e) => setFormData({...formData, escalation_rate: parseFloat(e.target.value) || 0})}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="location-factor">Location Factor</Label>
                <Input
                  id="location-factor"
                  type="number"
                  step="0.0001"
                  value={formData.location_factor}
                  onChange={(e) => setFormData({...formData, location_factor: parseFloat(e.target.value) || 1.0})}
                />
              </div>
            </div>
          </div>

          {/* Financial Summary */}
          <div className="space-y-4">
            <h4 className="font-medium">Financial Summary</h4>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="direct-costs">Direct Costs</Label>
                <Input
                  id="direct-costs"
                  type="number"
                  step="0.01"
                  value={formData.direct_costs}
                  onChange={(e) => setFormData({...formData, direct_costs: parseFloat(e.target.value) || 0})}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="indirect-costs">Indirect Costs</Label>
                <Input
                  id="indirect-costs"
                  type="number"
                  step="0.01"
                  value={formData.indirect_costs}
                  onChange={(e) => setFormData({...formData, indirect_costs: parseFloat(e.target.value) || 0})}
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="overhead-percentage">Overhead %</Label>
                <Input
                  id="overhead-percentage"
                  type="number"
                  step="0.01"
                  value={formData.overhead_percentage}
                  onChange={(e) => setFormData({...formData, overhead_percentage: parseFloat(e.target.value) || 0})}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="profit-percentage">Profit %</Label>
                <Input
                  id="profit-percentage"
                  type="number"
                  step="0.01"
                  value={formData.profit_percentage}
                  onChange={(e) => setFormData({...formData, profit_percentage: parseFloat(e.target.value) || 0})}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contingency-percentage">Contingency %</Label>
                <Input
                  id="contingency-percentage"
                  type="number"
                  step="0.01"
                  value={formData.contingency_percentage}
                  onChange={(e) => setFormData({...formData, contingency_percentage: parseFloat(e.target.value) || 0})}
                />
              </div>
            </div>
          </div>

          {/* Accuracy & Confidence */}
          <div className="space-y-4">
            <h4 className="font-medium">Accuracy & Confidence</h4>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="accuracy-level">Accuracy Level</Label>
                <Select value={formData.accuracy_level} onValueChange={(value) => setFormData({...formData, accuracy_level: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select accuracy level" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="preliminary">Preliminary (-30% / +50%)</SelectItem>
                    <SelectItem value="budget">Budget (-15% / +20%)</SelectItem>
                    <SelectItem value="definitive">Definitive (-5% / +10%)</SelectItem>
                    <SelectItem value="detailed">Detailed (-3% / +5%)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="confidence-level">Confidence Level (%)</Label>
                <Input
                  id="confidence-level"
                  type="number"
                  min="0"
                  max="100"
                  value={formData.confidence_level}
                  onChange={(e) => setFormData({...formData, confidence_level: parseInt(e.target.value) || 0})}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="currency">Currency</Label>
              <Select value={formData.currency} onValueChange={(value) => setFormData({...formData, currency: value})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CURRENCIES.map(c => <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? 'Creating...' : 'Create Estimate'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Add Estimate Item Modal Component
function AddEstimateItemModal({ estimateId, onSuccess }: { estimateId: string; onSuccess?: () => void }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchBOQItems, setSearchBOQItems] = useState('');
  const [selectedBoqItem, setSelectedBoqItem] = useState<any>(null);
  const [formData, setFormData] = useState({
    item_code: '',
    description: '',
    work_category: '',
    cost_type: '',
    quantity: 0,
    unit_of_measure: '',
    unit_rate: 0,
    crew_size: 0,
    productivity_rate: 0,
    labor_hours: 0,
    labor_rate: 0,
    labor_cost: 0,
    material_cost: 0,
    equipment_cost: 0,
    waste_factor: 0,
    difficulty_factor: 1.0,
    source: '',
    notes: ''
  });

  // Demo BOQ Items - in real app, fetch from API based on estimate's BOQ
  const boqItems = [
    { id: 1, code: "EXC-001", description: "Site Excavation", unit: "m³", rate: 45, category: "Earthwork" },
    { id: 2, code: "CON-001", description: "Concrete Grade 25", unit: "m³", rate: 120, category: "Concrete" },
    { id: 3, code: "STL-001", description: "Steel Reinforcement", unit: "kg", rate: 1.8, category: "Steel" },
    { id: 4, code: "FRM-001", description: "Concrete Formwork", unit: "m²", rate: 25, category: "Concrete" },
    { id: 5, code: "MSN-001", description: "Brick Masonry", unit: "m²", rate: 35, category: "Masonry" }
  ];

  const filteredBOQItems = boqItems.filter(item =>
    item.description.toLowerCase().includes(searchBOQItems.toLowerCase()) ||
    item.code.toLowerCase().includes(searchBOQItems.toLowerCase())
  );

  const totalCost = formData.quantity * formData.unit_rate;

  const handleSelectBOQItem = (item: any) => {
    setSelectedBoqItem(item);
    setFormData({
      ...formData,
      item_code: item.code,
      description: item.description,
      unit_of_measure: item.unit,
      unit_rate: item.rate,
      work_category: item.category.toLowerCase()
    });
    setSearchBOQItems('');
  };

  const handleSave = async () => {
    if (!formData.description || !formData.unit_of_measure || !formData.quantity || !formData.cost_type) {
      showError({
        title: 'Validation Error',
        message: 'Please fill in required fields: Description, Unit, Quantity, and Cost Type'
      });
      return;
    }

    setSaving(true);
    try {
      const userProfile = await authService.getUserProfile();
      const organizationId = userProfile.organizationId;

      const payload = {
        ...formData,
        estimate_id: estimateId,
        total_cost: totalCost
      };

      if (import.meta.env.DEV) {
        console.log('=== Adding Estimate Item ===');
        console.log('Estimate ID:', estimateId);
        console.log('Organization ID:', organizationId);
        console.log('Full Payload:', JSON.stringify(payload, null, 2));
      }

      await httpClient.post(`/api/v1/estimates/${estimateId}/items?organization_id=${organizationId}`, payload);

      showSuccess({
        title: 'Item Added',
        message: 'Estimate item added successfully!'
      });

      setOpen(false);
      if (onSuccess) onSuccess();
    } catch (error: any) {
      console.error('Failed to save estimate item:', error);
      showError({
        title: 'Failed to Add Item',
        message: error.message || 'Failed to save estimate item. Please try again.'
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>
          <Plus className="h-4 w-4 mr-2" />
          Add Item
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[900px] max-h-[90vh] overflow-y-auto modal-content">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus className="h-5 w-5" />
            Add Estimate Item
          </DialogTitle>
          <DialogDescription>
            Search BOQ items or create a new estimate line item
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Search BOQ Items */}
          <div className="space-y-4">
            <Label>Search BOQ Items</Label>
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by description or code..."
                className="pl-9"
                value={searchBOQItems}
                onChange={(e) => setSearchBOQItems(e.target.value)}
              />
            </div>

            {searchBOQItems && (
              <div className="border rounded-lg max-h-48 overflow-y-auto">
                {filteredBOQItems.length > 0 ? (
                  filteredBOQItems.map((item) => (
                    <div key={item.id} className="p-3 border-b last:border-b-0 hover:bg-muted/50 cursor-pointer">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-sm text-muted-foreground">{item.code}</span>
                            <Badge variant="outline" className="text-xs">{item.category}</Badge>
                          </div>
                          <p className="font-medium">{item.description}</p>
                          <p className="text-sm text-muted-foreground">
                            Unit: {item.unit} | Rate: ${item.rate}
                          </p>
                        </div>
                        <Button size="sm" onClick={() => handleSelectBOQItem(item)}>
                          Select
                        </Button>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="p-4 text-center text-muted-foreground">
                    No BOQ items found
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Item Details */}
          <div className="space-y-4 border-t pt-4">
            <h4 className="font-medium">Item Details</h4>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="item-code">Item Code</Label>
                <Input
                  id="item-code"
                  placeholder="e.g., EXC-001"
                  value={formData.item_code}
                  onChange={(e) => setFormData({...formData, item_code: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="work-category">Work Category</Label>
                <Select value={formData.work_category} onValueChange={(value) => setFormData({...formData, work_category: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="earthwork">Earthwork & Excavation</SelectItem>
                    <SelectItem value="concrete">Concrete Work</SelectItem>
                    <SelectItem value="masonry">Masonry</SelectItem>
                    <SelectItem value="steel">Steel & Reinforcement</SelectItem>
                    <SelectItem value="roofing">Roofing</SelectItem>
                    <SelectItem value="electrical">Electrical</SelectItem>
                    <SelectItem value="plumbing">Plumbing</SelectItem>
                    <SelectItem value="finishing">Finishing Work</SelectItem>
                    <SelectItem value="misc">Miscellaneous</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="item-description">Description</Label>
              <Textarea
                id="item-description"
                placeholder="Detailed description..."
                rows={2}
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="cost-type">Cost Type <span className="text-destructive">*</span></Label>
                <Select value={formData.cost_type} onValueChange={(value) => setFormData({...formData, cost_type: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select cost type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="labor">Labor</SelectItem>
                    <SelectItem value="material">Material</SelectItem>
                    <SelectItem value="equipment">Equipment</SelectItem>
                    <SelectItem value="subcontractor">Subcontractor</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="unit-of-measure">Unit of Measure</Label>
                <Select value={formData.unit_of_measure} onValueChange={(value) => setFormData({...formData, unit_of_measure: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select unit" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="m³">m³ - Cubic Meter</SelectItem>
                    <SelectItem value="m²">m² - Square Meter</SelectItem>
                    <SelectItem value="m">m - Linear Meter</SelectItem>
                    <SelectItem value="kg">kg - Kilogram</SelectItem>
                    <SelectItem value="ton">ton - Metric Ton</SelectItem>
                    <SelectItem value="each">each - Each</SelectItem>
                    <SelectItem value="hr">hr - Hour</SelectItem>
                    <SelectItem value="LS">LS - Lump Sum</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="quantity">Quantity</Label>
                <Input
                  id="quantity"
                  type="number"
                  step="0.0001"
                  value={formData.quantity}
                  onChange={(e) => setFormData({...formData, quantity: parseFloat(e.target.value) || 0})}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="unit-rate">Unit Rate</Label>
                <Input
                  id="unit-rate"
                  type="number"
                  step="0.01"
                  value={formData.unit_rate}
                  onChange={(e) => setFormData({...formData, unit_rate: parseFloat(e.target.value) || 0})}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="total-cost">Total Cost</Label>
                <Input
                  id="total-cost"
                  type="number"
                  value={totalCost.toFixed(2)}
                  readOnly
                  className="bg-muted"
                />
              </div>
            </div>
          </div>

          {/* Resource Details */}
          <div className="space-y-4 border-t pt-4">
            <h4 className="font-medium">Resource Details</h4>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="crew-size">Crew Size</Label>
                <Input
                  id="crew-size"
                  type="number"
                  value={formData.crew_size}
                  onChange={(e) => setFormData({...formData, crew_size: parseInt(e.target.value) || 0})}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="productivity-rate">Productivity Rate</Label>
                <Input
                  id="productivity-rate"
                  type="number"
                  step="0.0001"
                  value={formData.productivity_rate}
                  onChange={(e) => setFormData({...formData, productivity_rate: parseFloat(e.target.value) || 0})}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="labor-hours">Labor Hours</Label>
                <Input
                  id="labor-hours"
                  type="number"
                  step="0.01"
                  value={formData.labor_hours}
                  onChange={(e) => setFormData({...formData, labor_hours: parseFloat(e.target.value) || 0})}
                />
              </div>
            </div>
          </div>

          {/* Cost Components */}
          <div className="space-y-4 border-t pt-4">
            <h4 className="font-medium">Cost Components</h4>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="labor-rate">Labor Rate</Label>
                <Input
                  id="labor-rate"
                  type="number"
                  step="0.01"
                  value={formData.labor_rate}
                  onChange={(e) => setFormData({...formData, labor_rate: parseFloat(e.target.value) || 0})}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="labor-cost">Labor Cost</Label>
                <Input
                  id="labor-cost"
                  type="number"
                  step="0.01"
                  value={formData.labor_cost}
                  onChange={(e) => setFormData({...formData, labor_cost: parseFloat(e.target.value) || 0})}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="material-cost">Material Cost</Label>
                <Input
                  id="material-cost"
                  type="number"
                  step="0.01"
                  value={formData.material_cost}
                  onChange={(e) => setFormData({...formData, material_cost: parseFloat(e.target.value) || 0})}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="equipment-cost">Equipment Cost</Label>
                <Input
                  id="equipment-cost"
                  type="number"
                  step="0.01"
                  value={formData.equipment_cost}
                  onChange={(e) => setFormData({...formData, equipment_cost: parseFloat(e.target.value) || 0})}
                />
              </div>
            </div>
          </div>

          {/* Adjustments */}
          <div className="space-y-4 border-t pt-4">
            <h4 className="font-medium">Adjustments</h4>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="waste-factor">Waste Factor (%)</Label>
                <Input
                  id="waste-factor"
                  type="number"
                  step="0.01"
                  value={formData.waste_factor}
                  onChange={(e) => setFormData({...formData, waste_factor: parseFloat(e.target.value) || 0})}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="difficulty-factor">Difficulty Factor</Label>
                <Input
                  id="difficulty-factor"
                  type="number"
                  step="0.0001"
                  value={formData.difficulty_factor}
                  onChange={(e) => setFormData({...formData, difficulty_factor: parseFloat(e.target.value) || 1.0})}
                />
              </div>
            </div>
          </div>

          {/* Reference Information */}
          <div className="space-y-4 border-t pt-4">
            <h4 className="font-medium">Reference Information</h4>
            <div className="space-y-2">
              <Label htmlFor="source">Source</Label>
              <Input
                id="source"
                placeholder="e.g., RS Means 2024, Supplier Quote"
                value={formData.source}
                onChange={(e) => setFormData({...formData, source: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                placeholder="Additional notes..."
                rows={2}
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? 'Adding...' : 'Add Item'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Estimate Details Modal (View/Edit)
function EstimateDetailsModal({ mode, estimate, trigger, onSuccess }: { mode: 'view' | 'edit'; estimate: any; trigger: React.ReactNode; onSuccess?: () => void }) {
  const isReadOnly = mode === 'view';
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [estimateData, setEstimateData] = useState<any>(null);
  const [availableBOQs, setAvailableBOQs] = useState<any[]>([]);
  const [formData, setFormData] = useState({
    estimate_number: estimate?.estimate_number || '',
    estimate_name: estimate?.estimate_name || '',
    description: estimate?.description || '',
    version: estimate?.version || '1.0',
    estimate_date: estimate?.estimate_date || new Date().toISOString().split('T')[0],
    base_date: estimate?.base_date || '',
    escalation_rate: estimate?.escalation_rate || 0,
    location_factor: estimate?.location_factor || 1,
    direct_costs: estimate?.direct_costs || 0,
    indirect_costs: estimate?.indirect_costs || 0,
    overhead_percentage: estimate?.overhead_percentage || 0,
    overhead_amount: estimate?.overhead_amount || 0,
    profit_percentage: estimate?.profit_percentage || 0,
    profit_amount: estimate?.profit_amount || 0,
    contingency_percentage: estimate?.contingency_percentage || 0,
    contingency_amount: estimate?.contingency_amount || 0,
    total_estimate: estimate?.total_estimate || 0,
    accuracy_level: estimate?.accuracy_level || '',
    confidence_level: estimate?.confidence_level || 0,
    currency: estimate?.currency || 'USD',
    status: estimate?.status || 'draft',
    boq_id: estimate?.boq_id || ''
  });
  const [errors, setErrors] = useState<{[key: string]: string}>({});

  // Fetch BOQs when modal opens
  useEffect(() => {
    const fetchBOQs = async () => {
      if (open) {
        try {
          const userProfile = await authService.getUserProfile();
          const organizationId = userProfile.organizationId;
          const response = await httpClient.get(`/api/v1/boqs/?organization_id=${organizationId}`);

          // Handle response format - could be array or object with data property
          let boqList = [];
          if (Array.isArray(response.data)) {
            boqList = response.data;
          } else if (response.data?.data && Array.isArray(response.data.data)) {
            boqList = response.data.data;
          } else if (response.data) {
            boqList = [response.data];
          }

          setAvailableBOQs(boqList);
        } catch (error) {
          console.error('Failed to fetch BOQs:', error);
          setAvailableBOQs([]);
        }
      }
    };
    fetchBOQs();
  }, [open]);

  // Fetch estimate details when modal opens
  useEffect(() => {
    const fetchEstimateDetails = async () => {
      if (open && estimate?.id) {
        setLoading(true);
        try {
          const userProfile = await authService.getUserProfile();
          const organizationId = userProfile.organizationId;
          const response = await httpClient.get(`/api/v1/estimates/${estimate.id}?organization_id=${organizationId}`);
          setEstimateData(response.data);

          // Update formData with full estimate details
          setFormData({
            estimate_number: response.data.estimate_number || '',
            estimate_name: response.data.estimate_name || '',
            description: response.data.description || '',
            version: response.data.version || '1.0',
            estimate_date: response.data.estimate_date || new Date().toISOString().split('T')[0],
            base_date: response.data.base_date || '',
            escalation_rate: response.data.escalation_rate || 0,
            location_factor: response.data.location_factor || 1,
            direct_costs: response.data.direct_costs || 0,
            indirect_costs: response.data.indirect_costs || 0,
            overhead_percentage: response.data.overhead_percentage || 0,
            overhead_amount: response.data.overhead_amount || 0,
            profit_percentage: response.data.profit_percentage || 0,
            profit_amount: response.data.profit_amount || 0,
            contingency_percentage: response.data.contingency_percentage || 0,
            contingency_amount: response.data.contingency_amount || 0,
            total_estimate: response.data.total_estimate || 0,
            accuracy_level: response.data.accuracy_level || '',
            confidence_level: response.data.confidence_level || 0,
            currency: response.data.currency || 'USD',
            status: response.data.status || 'draft',
            boq_id: response.data.boq_id || ''
          });
        } catch (error) {
          console.error('Failed to fetch estimate details:', error);
        } finally {
          setLoading(false);
        }
      }
    };
    fetchEstimateDetails();
  }, [open, estimate?.id]);

  const handleSave = async () => {
    if (!formData.estimate_number || !formData.estimate_name) {
      showError({
        title: 'Validation Error',
        message: 'Please fill in required fields: Estimate Number and Estimate Name'
      });
      return;
    }

    setSaving(true);
    try {
      const userProfile = await authService.getUserProfile();
      const organizationId = userProfile.organizationId;

      const payload = { ...formData };

      if (import.meta.env.DEV) {
        console.log('Updating Estimate:', payload);
      }

      await httpClient.put(`/api/v1/estimates/${estimate.id}?organization_id=${organizationId}`, payload);

      showSuccess({
        title: 'Estimate Updated',
        message: 'Estimate updated successfully!'
      });

      setOpen(false);
      if (onSuccess) onSuccess();
    } catch (error: any) {
      console.error('Failed to update estimate:', error);
      showError({
        title: 'Update Failed',
        message: error.message || 'Failed to update estimate'
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto modal-content">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {mode === 'view' ? <Eye className="h-5 w-5" /> : <Edit className="h-5 w-5" />}
            {mode === 'view' ? 'View Estimate Details' : 'Edit Estimate Details'}
          </DialogTitle>
          <DialogDescription>
            {mode === 'view' ? 'View complete estimate information' : 'Update estimate information'}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-6 py-4">
          {loading ? (
            <div className="text-center py-8">Loading estimate details...</div>
          ) : (
            <>
              {/* Basic Information */}
              <div className="space-y-4">
                <h4 className="font-medium">Basic Information</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="est-number">Estimate Number</Label>
                    <Input
                      id="est-number"
                      value={formData.estimate_number}
                      onChange={(e) => !isReadOnly && setFormData({...formData, estimate_number: e.target.value})}
                      readOnly={isReadOnly}
                      className={isReadOnly ? 'bg-muted' : ''}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="est-name">Estimate Name</Label>
                    <Input
                      id="est-name"
                      value={formData.estimate_name}
                      onChange={(e) => !isReadOnly && setFormData({...formData, estimate_name: e.target.value})}
                      readOnly={isReadOnly}
                      className={isReadOnly ? 'bg-muted' : ''}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="est-description">Description</Label>
                  <Textarea
                    id="est-description"
                    value={formData.description}
                    onChange={(e) => !isReadOnly && setFormData({...formData, description: e.target.value})}
                    readOnly={isReadOnly}
                    className={isReadOnly ? 'bg-muted' : ''}
                    rows={3}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="est-version">Version</Label>
                    <Input
                      id="est-version"
                      value={formData.version}
                      onChange={(e) => !isReadOnly && setFormData({...formData, version: e.target.value})}
                      readOnly={isReadOnly}
                      className={isReadOnly ? 'bg-muted' : ''}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="est-linked-boq">Linked BOQ</Label>
                    {isReadOnly ? (
                      <Input
                        id="est-linked-boq"
                        value={estimate?.boq || 'No BOQ'}
                        readOnly
                        className="bg-muted"
                      />
                    ) : (
                      <Select
                        value={formData.boq_id}
                        onValueChange={(value) => setFormData({...formData, boq_id: value})}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select a BOQ" />
                        </SelectTrigger>
                        <SelectContent>
                          {availableBOQs.filter(boq => boq.boq_id || boq.id).map((boq) => (
                            <SelectItem key={boq.boq_id || boq.id} value={boq.boq_id || boq.id}>
                              {boq.boq_name || boq.name || 'Unnamed BOQ'}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="est-date">Estimate Date</Label>
                    <Input
                      id="est-date"
                      type="date"
                      value={formData.estimate_date}
                      onChange={(e) => !isReadOnly && setFormData({...formData, estimate_date: e.target.value})}
                      readOnly={isReadOnly}
                      className={isReadOnly ? 'bg-muted' : ''}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="est-base-date">Base Date</Label>
                    <Input
                      id="est-base-date"
                      type="date"
                      value={formData.base_date}
                      onChange={(e) => !isReadOnly && setFormData({...formData, base_date: e.target.value})}
                      readOnly={isReadOnly}
                      className={isReadOnly ? 'bg-muted' : ''}
                    />
                  </div>
                </div>
              </div>

              {/* Estimate Parameters */}
              <div className="space-y-4">
                <h4 className="font-medium">Estimate Parameters</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="est-escalation-rate">Escalation Rate (%)</Label>
                    <Input
                      id="est-escalation-rate"
                      type="number"
                      step="0.01"
                      value={formData.escalation_rate}
                      onChange={(e) => !isReadOnly && setFormData({...formData, escalation_rate: parseFloat(e.target.value) || 0})}
                      readOnly={isReadOnly}
                      className={isReadOnly ? 'bg-muted' : ''}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="est-location-factor">Location Factor</Label>
                    <Input
                      id="est-location-factor"
                      type="number"
                      step="0.0001"
                      value={formData.location_factor}
                      onChange={(e) => !isReadOnly && setFormData({...formData, location_factor: parseFloat(e.target.value) || 1.0})}
                      readOnly={isReadOnly}
                      className={isReadOnly ? 'bg-muted' : ''}
                    />
                  </div>
                </div>
              </div>

              {/* Financial Summary */}
              <div className="space-y-4">
                <h4 className="font-medium">Financial Summary</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="est-direct-costs">Direct Costs</Label>
                    <Input
                      id="est-direct-costs"
                      type="number"
                      step="0.01"
                      value={formData.direct_costs}
                      onChange={(e) => !isReadOnly && setFormData({...formData, direct_costs: parseFloat(e.target.value) || 0})}
                      readOnly={isReadOnly}
                      className={isReadOnly ? 'bg-muted' : ''}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="est-indirect-costs">Indirect Costs</Label>
                    <Input
                      id="est-indirect-costs"
                      type="number"
                      step="0.01"
                      value={formData.indirect_costs}
                      onChange={(e) => !isReadOnly && setFormData({...formData, indirect_costs: parseFloat(e.target.value) || 0})}
                      readOnly={isReadOnly}
                      className={isReadOnly ? 'bg-muted' : ''}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="est-overhead-percentage">Overhead %</Label>
                    <Input
                      id="est-overhead-percentage"
                      type="number"
                      step="0.01"
                      value={formData.overhead_percentage}
                      onChange={(e) => !isReadOnly && setFormData({...formData, overhead_percentage: parseFloat(e.target.value) || 0})}
                      readOnly={isReadOnly}
                      className={isReadOnly ? 'bg-muted' : ''}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="est-profit-percentage">Profit %</Label>
                    <Input
                      id="est-profit-percentage"
                      type="number"
                      step="0.01"
                      value={formData.profit_percentage}
                      onChange={(e) => !isReadOnly && setFormData({...formData, profit_percentage: parseFloat(e.target.value) || 0})}
                      readOnly={isReadOnly}
                      className={isReadOnly ? 'bg-muted' : ''}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="est-contingency-percentage">Contingency %</Label>
                    <Input
                      id="est-contingency-percentage"
                      type="number"
                      step="0.01"
                      value={formData.contingency_percentage}
                      onChange={(e) => !isReadOnly && setFormData({...formData, contingency_percentage: parseFloat(e.target.value) || 0})}
                      readOnly={isReadOnly}
                      className={isReadOnly ? 'bg-muted' : ''}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="est-overhead-amount">Overhead Amount</Label>
                    <Input
                      id="est-overhead-amount"
                      type="number"
                      step="0.01"
                      value={formData.overhead_amount}
                      onChange={(e) => !isReadOnly && setFormData({...formData, overhead_amount: parseFloat(e.target.value) || 0})}
                      readOnly={isReadOnly}
                      className={isReadOnly ? 'bg-muted' : ''}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="est-profit-amount">Profit Amount</Label>
                    <Input
                      id="est-profit-amount"
                      type="number"
                      step="0.01"
                      value={formData.profit_amount}
                      onChange={(e) => !isReadOnly && setFormData({...formData, profit_amount: parseFloat(e.target.value) || 0})}
                      readOnly={isReadOnly}
                      className={isReadOnly ? 'bg-muted' : ''}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="est-contingency-amount">Contingency Amount</Label>
                    <Input
                      id="est-contingency-amount"
                      type="number"
                      step="0.01"
                      value={formData.contingency_amount}
                      onChange={(e) => !isReadOnly && setFormData({...formData, contingency_amount: parseFloat(e.target.value) || 0})}
                      readOnly={isReadOnly}
                      className={isReadOnly ? 'bg-muted' : ''}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="est-total">Total Estimate</Label>
                  <Input
                    id="est-total"
                    type="number"
                    step="0.01"
                    value={formData.total_estimate}
                    onChange={(e) => !isReadOnly && setFormData({...formData, total_estimate: parseFloat(e.target.value) || 0})}
                    readOnly={isReadOnly}
                    className={`${isReadOnly ? 'bg-muted' : ''} font-medium text-lg`}
                  />
                </div>
              </div>

              {/* Additional Info */}
              <div className="space-y-4">
                <h4 className="font-medium">Additional Information</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="est-accuracy-level">Accuracy Level</Label>
                    <Input
                      id="est-accuracy-level"
                      value={formData.accuracy_level}
                      onChange={(e) => !isReadOnly && setFormData({...formData, accuracy_level: e.target.value})}
                      readOnly={isReadOnly}
                      className={isReadOnly ? 'bg-muted' : ''}
                      placeholder="e.g., Preliminary, Detailed"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="est-confidence-level">Confidence Level (%)</Label>
                    <Input
                      id="est-confidence-level"
                      type="number"
                      step="1"
                      min="0"
                      max="100"
                      value={formData.confidence_level}
                      onChange={(e) => !isReadOnly && setFormData({...formData, confidence_level: parseFloat(e.target.value) || 0})}
                      readOnly={isReadOnly}
                      className={isReadOnly ? 'bg-muted' : ''}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="est-currency">Currency</Label>
                    <Input
                      id="est-currency"
                      value={formData.currency}
                      onChange={(e) => !isReadOnly && setFormData({...formData, currency: e.target.value})}
                      readOnly={isReadOnly}
                      className={isReadOnly ? 'bg-muted' : ''}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="est-status">Status</Label>
                    <Input
                      id="est-status"
                      value={formData.status}
                      readOnly
                      className="bg-muted"
                    />
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          {mode === 'edit' && <Button onClick={handleSave}>Save Changes</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function Estimates() {
  const { canWrite, canApprove, canDelete } = usePermission();
  const orgCurrency = useOrgCurrency();
  const [activeTab, setActiveTab] = useState("estimates");
  const [selectedEstimate, setSelectedEstimate] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const { orgId } = useAuth();
  const { data: rawEstimates = [], isLoading: loading, refetch: refetchEstimates } = useEstimates(orgId);
  const { data: rawBoqsForEst = [] } = useBoqs(orgId);
  const { data: rawEstimateItems = [], isLoading: loadingItems, refetch: refetchEstimateItems } = useEstimateItems(selectedEstimate || null, orgId);

  const refreshEstimates = () => { refetchEstimates(); };
  const refreshEstimateItems = () => { refetchEstimateItems(); };

  const boqNameMap: Map<string, string> = useMemo(() => {
    const m = new Map<string, string>();
    (rawBoqsForEst as any[]).forEach((boq: any) => m.set(boq.boq_id, boq.boq_name || boq.name));
    return m;
  }, [rawBoqsForEst]);

  const estimates: any[] = useMemo(() => (rawEstimates as any[]).map((est: any) => ({
    id: est.estimate_id || est.id,
    name: est.estimate_name,
    boq: est.boq_id ? (boqNameMap.get(est.boq_id) || 'Unknown BOQ') : 'No BOQ',
    boq_id: est.boq_id,
    status: est.status,
    date: est.estimate_date,
    created_date: est.created_at ? new Date(est.created_at).toLocaleDateString() : 'N/A',
    value: est.total_estimate ? formatCurrency(parseFloat(est.total_estimate), orgCurrency) : formatCurrency(0, orgCurrency),
    total_estimate_raw: est.total_estimate || 0,
    ...est
  })), [rawEstimates, boqNameMap]);

  const estimateItems: any[] = useMemo(() => (rawEstimateItems as any[]).map((item: any) => ({
    id: item.estimate_item_id || item.id,
    code: item.item_code,
    description: item.description,
    category: item.work_category,
    unit: item.unit_of_measure,
    quantity: parseFloat(item.quantity) || 0,
    rate: parseFloat(item.unit_rate) || 0,
    cost: parseFloat(item.total_cost) || 0,
    costType: item.cost_type,
    ...item
  })), [rawEstimateItems]);

  // Auto-select first estimate when list loads
  useEffect(() => {
    if (estimates.length > 0 && !selectedEstimate) {
      setSelectedEstimate(estimates[0].id);
    }
  }, [estimates, selectedEstimate]);

  const filteredEstimates = estimates.filter(estimate => {
    const matchesSearch = estimate.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         estimate.boq?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         estimate.estimate_number?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === "all" || estimate.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const {
    currentPage: estimatesCurrentPage,
    totalPages: estimatesTotalPages,
    itemsPerPage: estimatesItemsPerPage,
    paginatedData: paginatedEstimates,
    setCurrentPage: setEstimatesCurrentPage,
    setItemsPerPage: setEstimatesItemsPerPage
  } = usePagination({ data: filteredEstimates, initialItemsPerPage: 10 });

  const filteredEstimateItems = estimateItems;

  const {
    currentPage: itemsCurrentPage,
    totalPages: itemsTotalPages,
    itemsPerPage: itemsItemsPerPage,
    paginatedData: paginatedEstimateItems,
    setCurrentPage: setItemsCurrentPage,
    setItemsPerPage: setItemsItemsPerPage
  } = usePagination({ data: filteredEstimateItems, initialItemsPerPage: 10 });

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "approved": return <Badge variant="default">Approved</Badge>;
      case "pending": return <Badge variant="secondary">Pending</Badge>;
      case "under-review": return <Badge className="bg-yellow-100 text-yellow-800">Under Review</Badge>;
      case "draft": return <Badge variant="outline">Draft</Badge>;
      default: return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getCostTypeBadge = (type: string) => {
    switch (type) {
      case "direct": return <Badge variant="default" className="text-xs">Direct</Badge>;
      case "indirect": return <Badge variant="secondary" className="text-xs">Indirect</Badge>;
      case "overhead": return <Badge variant="outline" className="text-xs">Overhead</Badge>;
      default: return <Badge variant="outline" className="text-xs">{type}</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Estimates Management</h2>
          <p className="text-muted-foreground">Project cost estimates and detailed line items</p>
        </div>
        {canWrite('/estimates') && <NewEstimateModal onSuccess={refreshEstimates} />}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <FileText className="h-4 w-4 text-blue-600" />
              Total Estimates
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-700">{estimates.length}</div>
            <p className="text-xs text-blue-600 mt-1">This year</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-600" />
              Won Projects
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-700">{estimates.filter(e => e.status === 'approved').length}</div>
            <p className="text-xs text-green-600 mt-1">
              {estimates.length > 0 ? Math.round((estimates.filter(e => e.status === 'approved').length / estimates.length) * 100) : 0}% success rate
            </p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-purple-600" />
              Estimate Value
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-purple-700">
              {formatCurrencyCompact(
                estimates.reduce((sum, e) => sum + (parseFloat(e.total_estimate) || 0), 0),
                orgCurrency
              )}
            </div>
            <p className="text-xs text-purple-600 mt-1">Total estimated</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Target className="h-4 w-4 text-emerald-600" />
              In Review
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-emerald-700">{estimates.filter(e => e.status === 'submitted' || e.status === 'under_review').length}</div>
            <p className="text-xs text-emerald-600 mt-1">Awaiting approval</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="h-5 w-5" />
            Estimate Management
          </CardTitle>
          <CardDescription>Manage your cost estimates and detailed line items</CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="estimates">Estimates</TabsTrigger>
              <TabsTrigger value="items">Estimate Items</TabsTrigger>
            </TabsList>

            <TabsContent value="estimates" className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search estimates..."
                    className="pl-9"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="under-review">Under Review</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Estimate Name</TableHead>
                    <TableHead>Linked BOQ</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created Date</TableHead>
                    <TableHead className="text-right">Total Value</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedEstimates.map((estimate) => (
                    <TableRow key={estimate.id} className={selectedEstimate === estimate.id ? "bg-muted/50" : ""}>
                      <TableCell className="font-medium">{estimate.name}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Calculator className="h-3 w-3" />
                          {estimate.boq}
                        </div>
                      </TableCell>
                      <TableCell>{getStatusBadge(estimate.status)}</TableCell>
                      <TableCell className="text-muted-foreground">{estimate.created_date}</TableCell>
                      <TableCell className="text-right font-medium">{estimate.value}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <EstimateDetailsModal
                            mode="view"
                            estimate={estimate}
                            trigger={
                              <Button variant="ghost" size="sm" title="View Estimate">
                                <Eye className="h-4 w-4" />
                              </Button>
                            }
                            onSuccess={refreshEstimates}
                          />
                          {canWrite('/estimates') && (
                            <EstimateDetailsModal
                              mode="edit"
                              estimate={estimate}
                              trigger={
                                <Button variant="ghost" size="sm" title="Edit Estimate">
                                  <Edit className="h-4 w-4" />
                                </Button>
                              }
                              onSuccess={refreshEstimates}
                            />
                          )}
                          {canApprove('/estimates') && (
                            <ApprovalProcessModal
                              type="Estimate"
                              item={estimate}
                              trigger={
                                <Button variant="ghost" size="sm" title="Process Approval">
                                  <CheckCircle className="h-4 w-4" />
                                </Button>
                              }
                              onSuccess={refreshEstimates}
                            />
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <Pagination
                currentPage={estimatesCurrentPage}
                totalPages={estimatesTotalPages}
                totalItems={filteredEstimates.length}
                itemsPerPage={estimatesItemsPerPage}
                onPageChange={setEstimatesCurrentPage}
                onItemsPerPageChange={setEstimatesItemsPerPage}
              />
            </TabsContent>

            <TabsContent value="items" className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="space-y-2">
                  <Label>Select Estimate</Label>
                  <Select value={selectedEstimate} onValueChange={setSelectedEstimate}>
                    <SelectTrigger className="w-64">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {estimates.filter(estimate => estimate.id).map((estimate) => (
                        <SelectItem key={estimate.id} value={estimate.id}>{estimate.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex-1" />
                {canWrite('/estimates') && <AddEstimateItemModal estimateId={selectedEstimate} onSuccess={refreshEstimateItems} />}
              </div>

              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Code</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Cost Type</TableHead>
                    <TableHead>Unit</TableHead>
                    <TableHead className="text-right">Quantity</TableHead>
                    <TableHead className="text-right">Rate</TableHead>
                    <TableHead className="text-right">Total Cost</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedEstimateItems.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell className="font-mono text-sm">{item.code}</TableCell>
                      <TableCell className="font-medium">{item.description}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">{item.category}</Badge>
                      </TableCell>
                      <TableCell>{getCostTypeBadge(item.costType)}</TableCell>
                      <TableCell>{item.unit}</TableCell>
                      <TableCell className="text-right">{item.quantity.toLocaleString()}</TableCell>
                      <TableCell className="text-right">{formatCurrency(item.rate, orgCurrency)}</TableCell>
                      <TableCell className="text-right font-medium">{formatCurrency(item.cost, orgCurrency)}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {canWrite('/estimates') && (
                            <EstimateItemEditModal
                              item={item}
                              estimateId={selectedEstimate}
                              trigger={
                                <Button variant="ghost" size="sm" title="Edit Item">
                                  <Edit className="h-4 w-4" />
                                </Button>
                              }
                              onSuccess={refreshEstimateItems}
                            />
                          )}
                          {canDelete('/estimates') && (
                            <EstimateItemDeleteModal
                              item={item}
                              estimateId={selectedEstimate}
                              trigger={
                                <Button variant="ghost" size="sm" title="Delete Item" className="text-destructive hover:text-destructive">
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              }
                              onSuccess={refreshEstimateItems}
                            />
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <Pagination
                currentPage={itemsCurrentPage}
                totalPages={itemsTotalPages}
                totalItems={estimateItems.length}
                itemsPerPage={itemsItemsPerPage}
                onPageChange={setItemsCurrentPage}
                onItemsPerPageChange={setItemsItemsPerPage}
              />

              <div className="flex justify-between items-center pt-4 border-t">
                <div className="text-sm text-muted-foreground">
                  Total: {estimateItems.length} items
                </div>
                <div className="text-xl font-bold">
                  Total Cost: {formatCurrency(estimateItems.reduce((sum, item) => sum + item.cost, 0), orgCurrency)}
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}

// Bid Submission Modal
function BidSubmissionModal({ bid, trigger, onSuccess }: { bid: any; trigger: React.ReactNode; onSuccess?: () => void }) {
  const [submissionData, setSubmissionData] = useState({
    status: 'submitted',
    documents_submitted: [] as Array<{name: string; file_id: string}>,
    submission_location: ''
  });
  const [documentName, setDocumentName] = useState('');
  const [uploading, setUploading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<FileList | null>(null);
  const [errors, setErrors] = useState<{[key: string]: boolean}>({});

  const handleFileUpload = async (file: File, fileName: string) => {
    if (!fileName.trim()) {
      alert('Please enter a document name first');
      return;
    }

    setUploading(true);

    try {
      const token = authService.getAccessToken();
      const userProfile = await authService.getUserProfile();
      const organizationId = userProfile.organizationId;
      const userId = userProfile.id;

      if (import.meta.env.DEV) {
        console.log('File upload - User ID:', userId, 'Organization ID:', organizationId);
      }

      const formData = new FormData();
      formData.append('file', file);

      // Build URL with parameters
      const uploadParams = new URLSearchParams({
        organization_id: organizationId || '',
        file_category: 'bid_document',
        related_entity_type: 'Bid Submission',
      });

      // Only add related_entity_id if it's a valid UUID format (not demo numeric ID)
      const isValidUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(bid?.id);
      if (isValidUUID && bid?.id) {
        uploadParams.append('related_entity_id', bid.id);
      }

      const response = await fetch(
        getBackendUrl(`/api/v1/files/upload?${uploadParams.toString()}`),
        {
          method: 'POST',
          body: formData,
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        if (import.meta.env.DEV) {
          console.error('Upload error details:', errorData);
        }
        throw new Error(`Upload failed for ${file.name}: ${errorData.detail || response.statusText}`);
      }

      const { file_id } = await response.json();

      // Add uploaded document to the list
      setSubmissionData({
        ...submissionData,
        documents_submitted: [...submissionData.documents_submitted, { name: fileName.trim(), file_id }]
      });

    } catch (error) {
      console.error('File upload error:', error);
      alert('Failed to upload file. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadDocument = (file_id: string) => {
    window.open(getBackendUrl(`/api/v1/files/download/${file_id}`), '_blank');
  };

  const handleRemoveDocument = async (index: number, file_id: string) => {
    try {
      const token = authService.getAccessToken();

      const response = await fetch(getBackendUrl(`/api/v1/files/${file_id}`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Delete failed');
      }

      setSubmissionData({
        ...submissionData,
        documents_submitted: submissionData.documents_submitted.filter((_, i) => i !== index)
      });
    } catch (error) {
      console.error('File delete error:', error);
      alert('Failed to delete file. Please try again.');
    }
  };

  const handleSubmit = async () => {
    // Validate required fields
    const validationErrors: {[key: string]: boolean} = {};

    if (!submissionData.submission_location) validationErrors.submission_location = true;
    if (submissionData.documents_submitted.length === 0) validationErrors.documents = true;

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      showError({
        title: 'Validation Error',
        message: 'Please provide submission location and upload at least one document'
      });
      return;
    }

    // Clear errors if validation passes
    setErrors({});

    try{
      const token = authService.getAccessToken();

      // Prepare submission payload
      const payload = {
        status: 'submitted',
        documents_submitted: submissionData.documents_submitted, // Array of {name, file_id} objects
        submission_location: submissionData.submission_location
      };

      if (import.meta.env.DEV) {
        console.log('Submit Bid Payload:', {
          bid_id: bid?.id,
          endpoint: `/api/v1/bids/${bid?.id}/submit`,
          payload: payload
        });
      }

      const response = await fetch(getBackendUrl(`/api/v1/bids/${bid?.id}/submit`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error('Submission failed');
      }

      showSuccess({
        title: 'Bid Submitted',
        message: 'Bid submitted successfully!'
      });

      // Trigger data refresh
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error('Bid submission error:', error);
      showError({
        title: 'Submission Failed',
        message: 'Failed to submit bid. Please try again.'
      });
    }
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        {trigger}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px] modal-content">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5" />
            Submit Bid
          </DialogTitle>
          <DialogDescription>
            Submit bid with required documents and location information
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="p-4 bg-muted/50 rounded-lg">
            <h4 className="font-medium">Bid Details</h4>
            <p className="text-sm text-muted-foreground mt-1">
              Project: {bid?.project}
            </p>
            <p className="text-sm text-muted-foreground">
              Client: {bid?.client}
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="submission-location" className={errors.submission_location ? 'text-red-600' : ''}>
              Submission Location <span className="text-red-600">*</span>
            </Label>
            <Input
              id="submission-location"
              placeholder="e.g., Client Portal, Physical Office, Email"
              value={submissionData.submission_location}
              onChange={(e) => {
                setSubmissionData({...submissionData, submission_location: e.target.value});
                if (errors.submission_location) setErrors({...errors, submission_location: false});
              }}
              className={errors.submission_location ? 'border-red-600 focus-visible:ring-red-600' : ''}
            />
            {errors.submission_location && <p className="text-xs text-red-600">Submission location is required</p>}
          </div>

          <div className="space-y-3">
            <Label className={errors.documents ? 'text-red-600' : ''}>
              Documents Submitted <span className="text-red-600">*</span>
            </Label>
            {errors.documents && <p className="text-xs text-red-600">At least one document is required</p>}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Input
                  id="file-upload-input"
                  type="file"
                  multiple
                  onChange={(e) => setSelectedFiles(e.target.files)}
                  disabled={uploading}
                  className="flex-1"
                />
              </div>

              {selectedFiles && selectedFiles.length > 0 && (
                <div className="space-y-2 mt-2">
                  <p className="text-sm font-medium">Selected Files ({selectedFiles.length}):</p>
                  {Array.from(selectedFiles).map((file, index) => (
                    <div key={index} className="flex items-center gap-2 p-2 bg-muted/30 rounded border">
                      <FileText className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                      <Input
                        placeholder="Enter document name"
                        defaultValue={file.name}
                        id={`doc-name-${index}`}
                        className="flex-1"
                      />
                      <Button
                        type="button"
                        size="sm"
                        onClick={() => {
                          const nameInput = document.getElementById(`doc-name-${index}`) as HTMLInputElement;
                          const docName = nameInput?.value || file.name;
                          handleFileUpload(file, docName);
                        }}
                        disabled={uploading}
                      >
                        <Upload className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {submissionData.documents_submitted.length > 0 && (
              <div className="space-y-2 mt-4">
                <p className="text-sm font-medium">Uploaded Documents ({submissionData.documents_submitted.length}):</p>
                {submissionData.documents_submitted.map((doc, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-green-50 rounded border border-green-200">
                    <div className="flex items-center gap-2 flex-1">
                      <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                      <span className="text-sm font-medium">{doc.name}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDownloadDocument(doc.file_id)}
                        title="Download"
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveDocument(index, doc.file_id)}
                        title="Remove"
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start gap-2">
              <CheckCircle className="h-4 w-4 text-blue-600 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-blue-900">Submission Action</p>
                <p className="text-blue-700">
                  This bid will be marked as submitted and the status will be updated accordingly.
                </p>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
          <Button
            onClick={handleSubmit}
            disabled={!submissionData.submission_location || submissionData.documents_submitted.length === 0}
          >
            Submit Bid
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Bid Status Update Modal
function BidStatusUpdateModal({ bid, trigger, onSuccess }: { bid: any; trigger: React.ReactNode; onSuccess?: () => void }) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState(bid?.status || 'under_review');
  const [updating, setUpdating] = useState(false);

  const statusOptions = [
    { value: 'under_review', label: 'Under Review', description: 'Bid is being reviewed', color: 'bg-yellow-100 text-yellow-800' },
    { value: 'accepted', label: 'Accepted', description: 'Bid has been accepted', color: 'bg-green-100 text-green-800' },
    { value: 'rejected', label: 'Rejected', description: 'Bid has been rejected', color: 'bg-red-100 text-red-800' },
    { value: 'withdrawn', label: 'Withdrawn', description: 'Bid has been withdrawn', color: 'bg-orange-100 text-orange-800' }
  ];

  const handleUpdateStatus = async () => {
    setUpdating(true);

    try {
      const userProfile = await authService.getUserProfile();
      const organizationId = userProfile.organizationId;

      await httpClient.post(
        `/api/v1/bids/${bid.id}/status?organization_id=${organizationId}`,
        { status: selectedStatus }
      );

      showSuccess({
        title: 'Status Updated',
        message: 'Bid status has been updated successfully!'
      });

      setIsOpen(false);

      // Trigger data refresh
      if (onSuccess) {
        onSuccess();
      }
    } catch (error: any) {
      console.error('Failed to update bid status:', error);
      showError({
        title: 'Update Failed',
        message: error.message || 'Failed to update bid status. Please try again.'
      });
    } finally {
      setUpdating(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        {trigger}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px] modal-content">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Update Bid Status
          </DialogTitle>
          <DialogDescription>
            Change the status of this bid opportunity
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="p-4 bg-muted/50 rounded-lg">
            <h4 className="font-medium">Bid Details</h4>
            <p className="text-sm text-muted-foreground mt-1">
              Project: {bid?.project || bid?.project_name}
            </p>
            <p className="text-sm text-muted-foreground">
              Client: {bid?.client || bid?.owner_name}
            </p>
          </div>

          <div className="space-y-3">
            <Label>Select New Status</Label>
            <div className="space-y-2">
              {statusOptions.map((option) => (
                <div
                  key={option.value}
                  className={`p-3 border rounded-lg cursor-pointer transition-all ${
                    selectedStatus === option.value
                      ? 'border-primary bg-primary/5'
                      : 'border-muted hover:border-primary/50'
                  }`}
                  onClick={() => setSelectedStatus(option.value)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <input
                        type="radio"
                        checked={selectedStatus === option.value}
                        onChange={() => setSelectedStatus(option.value)}
                        className="h-4 w-4"
                      />
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{option.label}</span>
                          <Badge className={option.color}>{option.label}</Badge>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">{option.description}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setIsOpen(false)} disabled={updating}>Cancel</Button>
          <Button onClick={handleUpdateStatus} disabled={updating}>
            {updating ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Updating...
              </>
            ) : (
              'Update Status'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// New Bid Modal Component
function NewBidModal({ onSuccess }: { onSuccess?: () => void }) {
  const orgCurrency = useOrgCurrency();
  const [formData, setFormData] = useState({
    project_name: '',
    bid_number: '',
    contract_type: '',
    project_category: '',
    project_location: '',
    project_description: '',
    owner_name: '',
    client_owner_type: '',
    owner_contact: '',
    contact_email: '',
    project_duration: '',
    currency: orgCurrency,
    total_bid_amount: '',
    bid_due_date: '',
    bid_opening_date: '',
    bid_bond_required: false,
    bid_bond_percentage: 0,
    bid_bond_amount: 0,
    performance_bond_required: false,
    payment_bond_required: false,
    source_of_info: '',
    prequalification: false,
    special_req_notes: '',
    general_liability_required: false,
    workers_comp_required: false,
    prevailing_wage: false,
    mbe_wbe_requirements: '',
    disadvantaged_business_goals: 0,
    environmental_requirements: '',
    safety_requirements: '',
    drug_testing_required: false,
    license_requirements: '',
    certification_requirements: '',
    union_requirements: '',
    show_mbe_wbe: false,
    show_disadvantaged_business: false,
    show_environmental: false,
    show_safety: false,
    show_license: false,
    show_certification: false,
    show_union: false,
    status: 'draft'
  });

  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const [errors, setErrors] = useState<{[key: string]: boolean}>({});

  const handleBondTypeChange = (value: string) => {
    setFormData({
      ...formData,
      bid_bond_required: value === 'bid' || value === 'both',
      performance_bond_required: value === 'performance' || value === 'both',
      payment_bond_required: value === 'payment' || value === 'all'
    });
  };

  const handleSave = async () => {
    // Validate required fields
    const validationErrors: {[key: string]: boolean} = {};

    if (!formData.project_name) validationErrors.project_name = true;
    if (!formData.bid_number) validationErrors.bid_number = true;

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      showError({
        title: 'Validation Error',
        message: 'Please fill in all required fields highlighted in red'
      });
      return;
    }

    // Clear errors if validation passes
    setErrors({});
    setSaving(true);

    try {
      // Generate a placeholder UUID for project_id (temporary until projects are created)
      const generateUUID = () => {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
          const r = Math.random() * 16 | 0;
          const v = c === 'x' ? r : (r & 0x3 | 0x8);
          return v.toString(16);
        });
      };

      // Prepare payload for backend
      const payload = {
        project_id: generateUUID(),
        project_name: formData.project_name,
        bid_number: formData.bid_number,
        contract_type: formData.contract_type,
        project_category: formData.project_category,
        project_location: formData.project_location,
        project_description: formData.project_description,
        owner_name: formData.owner_name,
        client_owner_type: formData.client_owner_type,
        owner_contact: formData.owner_contact,
        contact_email: formData.contact_email,
        project_duration: formData.project_duration ? parseInt(formData.project_duration) : null,
        currency: formData.currency,
        total_bid_amount: formData.total_bid_amount ? parseFloat(formData.total_bid_amount) : null,
        bid_due_date: formData.bid_due_date || null,
        bid_opening_date: formData.bid_opening_date || null,
        bid_bond_required: formData.bid_bond_required,
        bid_bond_percentage: formData.bid_bond_percentage,
        bid_bond_amount: formData.bid_bond_amount,
        performance_bond_required: formData.performance_bond_required,
        payment_bond_required: formData.payment_bond_required,
        source_of_info: formData.source_of_info,
        prequalification: formData.prequalification,
        special_req_notes: formData.special_req_notes,
        general_liability_required: formData.general_liability_required,
        workers_comp_required: formData.workers_comp_required,
        prevailing_wage: formData.prevailing_wage,
        mbe_wbe_requirements: formData.mbe_wbe_requirements,
        disadvantaged_business_goals: formData.disadvantaged_business_goals,
        environmental_requirements: formData.environmental_requirements,
        safety_requirements: formData.safety_requirements,
        drug_testing_required: formData.drug_testing_required,
        license_requirements: formData.license_requirements,
        certification_requirements: formData.certification_requirements,
        union_requirements: formData.union_requirements,
        status: formData.status
      };

      // Get organization_id from user profile
      const userProfile = await authService.getUserProfile();
      const organizationId = userProfile.organizationId;

      if (!organizationId) {
        throw new Error('Organization ID not found. Please contact support.');
      }

      if (import.meta.env.DEV) {
        console.log('Creating bid with payload:', JSON.stringify(payload, null, 2));
        console.log('Organization ID:', organizationId);
      }

      // Use httpClient which handles authentication automatically
      // organization_id is passed as query parameter
      await httpClient.post(`/api/v1/bids?organization_id=${organizationId}`, payload);

      // Reset form
      setFormData({
        project_name: '',
        bid_number: '',
        contract_type: '',
        project_category: '',
        project_location: '',
        project_description: '',
        owner_name: '',
        client_owner_type: '',
        owner_contact: '',
        contact_email: '',
        project_duration: '',
        currency: orgCurrency,
        total_bid_amount: '',
        bid_due_date: '',
        bid_opening_date: '',
        bid_bond_required: false,
        bid_bond_percentage: 0,
        bid_bond_amount: 0,
        performance_bond_required: false,
        payment_bond_required: false,
        source_of_info: '',
        prequalification: false,
        special_req_notes: '',
        general_liability_required: false,
        workers_comp_required: false,
        prevailing_wage: false,
        mbe_wbe_requirements: '',
        disadvantaged_business_goals: 0,
        environmental_requirements: '',
        safety_requirements: '',
        drug_testing_required: false,
        license_requirements: '',
        certification_requirements: '',
        union_requirements: '',
        show_mbe_wbe: false,
        show_disadvantaged_business: false,
        show_environmental: false,
        show_safety: false,
        show_license: false,
        show_certification: false,
        show_union: false,
        status: 'draft'
      });
      setOpen(false);

      showSuccess({
        title: 'Bid Created',
        message: 'Bid opportunity created successfully!'
      });

      // Trigger data refresh
      if (onSuccess) {
        onSuccess();
      }

    } catch (error) {
      console.error('Error creating bid:', error);
      showError({
        title: 'Creation Failed',
        message: `Failed to create bid opportunity: ${error instanceof Error ? error.message : 'Unknown error'}`
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>
          <Gavel className="h-4 w-4 mr-2" />
          New Bid
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto modal-content">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Gavel className="h-5 w-5" />
            Add New Bid Opportunity
          </DialogTitle>
          <DialogDescription>
            Record a new bidding opportunity with all relevant details
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Project Information */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Building className="h-4 w-4 text-primary" />
              <h4 className="font-medium">Project Information</h4>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="project-name" className={errors.project_name ? 'text-red-600' : ''}>
                  Project Name <span className="text-red-600">*</span>
                </Label>
                <Input
                  id="project-name"
                  placeholder="e.g., Highway Bridge Construction"
                  value={formData.project_name}
                  onChange={(e) => {
                    setFormData({...formData, project_name: e.target.value});
                    if (errors.project_name) setErrors({...errors, project_name: false});
                  }}
                  className={errors.project_name ? 'border-red-600 focus-visible:ring-red-600' : ''}
                />
                {errors.project_name && <p className="text-xs text-red-600">Project name is required</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="bid-number" className={errors.bid_number ? 'text-red-600' : ''}>
                  Bid Number <span className="text-red-600">*</span>
                </Label>
                <Input
                  id="bid-number"
                  placeholder="e.g., BID-2024-001"
                  value={formData.bid_number}
                  onChange={(e) => {
                    setFormData({...formData, bid_number: e.target.value});
                    if (errors.bid_number) setErrors({...errors, bid_number: false});
                  }}
                  className={errors.bid_number ? 'border-red-600 focus-visible:ring-red-600' : ''}
                />
                {errors.bid_number && <p className="text-xs text-red-600">Bid number is required</p>}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="nature-of-project">Nature of Project</Label>
                <Select value={formData.contract_type} onValueChange={(value) => setFormData({...formData, contract_type: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select project nature" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="new-construction">New Construction</SelectItem>
                    <SelectItem value="renovation">Renovation</SelectItem>
                    <SelectItem value="expansion">Expansion</SelectItem>
                    <SelectItem value="infrastructure">Infrastructure</SelectItem>
                    <SelectItem value="maintenance">Maintenance</SelectItem>
                    <SelectItem value="demolition">Demolition</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="project-category">Project Category</Label>
                <Select value={formData.project_category} onValueChange={(value) => setFormData({...formData, project_category: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="residential">Residential</SelectItem>
                    <SelectItem value="commercial">Commercial</SelectItem>
                    <SelectItem value="industrial">Industrial</SelectItem>
                    <SelectItem value="infrastructure">Infrastructure</SelectItem>
                    <SelectItem value="healthcare">Healthcare</SelectItem>
                    <SelectItem value="education">Education</SelectItem>
                    <SelectItem value="government">Government</SelectItem>
                    <SelectItem value="mixed-use">Mixed-Use</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="project-location">Project Location</Label>
              <Input
                id="project-location"
                placeholder="e.g., Downtown Business District, City, State"
                value={formData.project_location}
                onChange={(e) => setFormData({...formData, project_location: e.target.value})}
              />
            </div>
          </div>

          {/* Client Information */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <User className="h-4 w-4 text-primary" />
              <h4 className="font-medium">Client Information</h4>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="client-name">Client Name</Label>
                <Input
                  id="client-name"
                  placeholder="e.g., State Department of Transportation"
                  value={formData.owner_name}
                  onChange={(e) => setFormData({...formData, owner_name: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="client-type">Client Type</Label>
                <Select value={formData.client_owner_type} onValueChange={(value) => setFormData({...formData, client_owner_type: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select client type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="government">Government</SelectItem>
                    <SelectItem value="private">Private</SelectItem>
                    <SelectItem value="public">Public Entity</SelectItem>
                    <SelectItem value="non-profit">Non-Profit</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="contact-person">Contact Person</Label>
                <Input
                  id="contact-person"
                  placeholder="e.g., John Smith"
                  value={formData.owner_contact}
                  onChange={(e) => setFormData({...formData, owner_contact: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contact-email">Contact Email</Label>
                <Input
                  id="contact-email"
                  type="email"
                  placeholder="e.g., john.smith@client.com"
                  value={formData.contact_email}
                  onChange={(e) => setFormData({...formData, contact_email: e.target.value})}
                />
              </div>
            </div>
          </div>

          {/* Project Details */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-primary" />
              <h4 className="font-medium">Project Timeline & Budget</h4>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="estimated-duration">Est. Duration (Months)</Label>
                <Input
                  id="estimated-duration"
                  type="number"
                  placeholder="12"
                  value={formData.project_duration}
                  onChange={(e) => setFormData({...formData, project_duration: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="currency">Currency</Label>
                <Select value={formData.currency} onValueChange={(value) => setFormData({...formData, currency: value})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CURRENCIES.map(c => <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="estimated-value">Estimated Value</Label>
                <Input
                  id="estimated-value"
                  type="number"
                  placeholder="4200000"
                  value={formData.total_bid_amount}
                  onChange={(e) => setFormData({...formData, total_bid_amount: e.target.value})}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="bid-deadline">Bid Submission Deadline</Label>
                <Input
                  id="bid-deadline"
                  type="datetime-local"
                  value={formData.bid_due_date}
                  onChange={(e) => setFormData({...formData, bid_due_date: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="bid-opening-date">Bid Opening Date</Label>
                <Input
                  id="bid-opening-date"
                  type="datetime-local"
                  value={formData.bid_opening_date}
                  onChange={(e) => setFormData({...formData, bid_opening_date: e.target.value})}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="bond-required">Bond Required</Label>
              <Select onValueChange={handleBondTypeChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Select if bond required" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Bond Required</SelectItem>
                  <SelectItem value="bid">Bid Bond Only</SelectItem>
                  <SelectItem value="performance">Performance Bond Only</SelectItem>
                  <SelectItem value="payment">Payment Bond Only</SelectItem>
                  <SelectItem value="both">Bid & Performance Bond</SelectItem>
                  <SelectItem value="all">All Bonds Required</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {formData.bid_bond_required && (
              <div className="grid grid-cols-2 gap-4 pl-4 border-l-2 border-primary/20">
                <div className="space-y-2">
                  <Label htmlFor="bid-bond-percentage">Bid Bond Percentage (%)</Label>
                  <Input
                    id="bid-bond-percentage"
                    type="number"
                    placeholder="5"
                    value={formData.bid_bond_percentage}
                    onChange={(e) => setFormData({...formData, bid_bond_percentage: parseFloat(e.target.value) || 0})}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="bid-bond-amount">Bid Bond Amount</Label>
                  <Input
                    id="bid-bond-amount"
                    type="number"
                    placeholder="210000"
                    value={formData.bid_bond_amount}
                    onChange={(e) => setFormData({...formData, bid_bond_amount: parseFloat(e.target.value) || 0})}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Compliance Requirements */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-primary" />
              <h4 className="font-medium">Compliance Requirements</h4>
            </div>

            <div className="space-y-3 p-4 border rounded-lg">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="general-liability"
                  checked={formData.general_liability_required}
                  onChange={(e) => setFormData({...formData, general_liability_required: e.target.checked})}
                  className="h-4 w-4"
                />
                <Label htmlFor="general-liability" className="font-normal cursor-pointer">General Liability Insurance Required</Label>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="workers-comp"
                  checked={formData.workers_comp_required}
                  onChange={(e) => setFormData({...formData, workers_comp_required: e.target.checked})}
                  className="h-4 w-4"
                />
                <Label htmlFor="workers-comp" className="font-normal cursor-pointer">Workers Compensation Required</Label>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="prevailing-wage"
                  checked={formData.prevailing_wage}
                  onChange={(e) => setFormData({...formData, prevailing_wage: e.target.checked})}
                  className="h-4 w-4"
                />
                <Label htmlFor="prevailing-wage" className="font-normal cursor-pointer">Prevailing Wage</Label>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="drug-testing"
                  checked={formData.drug_testing_required}
                  onChange={(e) => setFormData({...formData, drug_testing_required: e.target.checked})}
                  className="h-4 w-4"
                />
                <Label htmlFor="drug-testing" className="font-normal cursor-pointer">Drug Testing Required</Label>
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="mbe-wbe-check"
                    checked={formData.show_mbe_wbe}
                    onChange={(e) => setFormData({...formData, show_mbe_wbe: e.target.checked})}
                    className="h-4 w-4 flex-shrink-0"
                  />
                  <Label htmlFor="mbe-wbe-check" className="font-normal cursor-pointer">MBE/WBE Requirements</Label>
                </div>
                {formData.show_mbe_wbe && (
                  <div className="pl-6 pr-2">
                    <Textarea
                      id="mbe-wbe"
                      placeholder="Enter MBE/WBE requirements..."
                      rows={2}
                      value={formData.mbe_wbe_requirements}
                      onChange={(e) => setFormData({...formData, mbe_wbe_requirements: e.target.value})}
                    />
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="disadvantaged-business-check"
                    checked={formData.show_disadvantaged_business}
                    onChange={(e) => setFormData({...formData, show_disadvantaged_business: e.target.checked})}
                    className="h-4 w-4 flex-shrink-0"
                  />
                  <Label htmlFor="disadvantaged-business-check" className="font-normal cursor-pointer">Disadvantaged Business Goals</Label>
                </div>
                {formData.show_disadvantaged_business && (
                  <div className="pl-6 pr-2">
                    <Label htmlFor="disadvantaged-business">Percentage (%)</Label>
                    <Input
                      id="disadvantaged-business"
                      type="number"
                      placeholder="0"
                      value={formData.disadvantaged_business_goals}
                      onChange={(e) => setFormData({...formData, disadvantaged_business_goals: parseFloat(e.target.value) || 0})}
                    />
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="environmental-check"
                    checked={formData.show_environmental}
                    onChange={(e) => setFormData({...formData, show_environmental: e.target.checked})}
                    className="h-4 w-4 flex-shrink-0"
                  />
                  <Label htmlFor="environmental-check" className="font-normal cursor-pointer">Environmental Requirements</Label>
                </div>
                {formData.show_environmental && (
                  <div className="pl-6 pr-2">
                    <Textarea
                      id="environmental-req"
                      placeholder="Enter environmental requirements..."
                      rows={2}
                      value={formData.environmental_requirements}
                      onChange={(e) => setFormData({...formData, environmental_requirements: e.target.value})}
                    />
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="safety-check"
                    checked={formData.show_safety}
                    onChange={(e) => setFormData({...formData, show_safety: e.target.checked})}
                    className="h-4 w-4 flex-shrink-0"
                  />
                  <Label htmlFor="safety-check" className="font-normal cursor-pointer">Safety Requirements</Label>
                </div>
                {formData.show_safety && (
                  <div className="pl-6 pr-2">
                    <Textarea
                      id="safety-req"
                      placeholder="Enter safety requirements..."
                      rows={2}
                      value={formData.safety_requirements}
                      onChange={(e) => setFormData({...formData, safety_requirements: e.target.value})}
                    />
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="license-check"
                    checked={formData.show_license}
                    onChange={(e) => setFormData({...formData, show_license: e.target.checked})}
                    className="h-4 w-4 flex-shrink-0"
                  />
                  <Label htmlFor="license-check" className="font-normal cursor-pointer">License Requirements</Label>
                </div>
                {formData.show_license && (
                  <div className="pl-6 pr-2">
                    <Textarea
                      id="license-req"
                      placeholder="Enter license requirements..."
                      rows={2}
                      value={formData.license_requirements}
                      onChange={(e) => setFormData({...formData, license_requirements: e.target.value})}
                    />
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="certification-check"
                    checked={formData.show_certification}
                    onChange={(e) => setFormData({...formData, show_certification: e.target.checked})}
                    className="h-4 w-4 flex-shrink-0"
                  />
                  <Label htmlFor="certification-check" className="font-normal cursor-pointer">Certification Requirements</Label>
                </div>
                {formData.show_certification && (
                  <div className="pl-6 pr-2">
                    <Textarea
                      id="certification-req"
                      placeholder="Enter certification requirements..."
                      rows={2}
                      value={formData.certification_requirements}
                      onChange={(e) => setFormData({...formData, certification_requirements: e.target.value})}
                    />
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="union-check"
                    checked={formData.show_union}
                    onChange={(e) => setFormData({...formData, show_union: e.target.checked})}
                    className="h-4 w-4 flex-shrink-0"
                  />
                  <Label htmlFor="union-check" className="font-normal cursor-pointer">Union Requirements</Label>
                </div>
                {formData.show_union && (
                  <div className="pl-6 pr-2">
                    <Textarea
                      id="union-req"
                      placeholder="Enter union requirements..."
                      rows={2}
                      value={formData.union_requirements}
                      onChange={(e) => setFormData({...formData, union_requirements: e.target.value})}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Source & Additional Info */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <FileText className="h-4 w-4 text-primary" />
              <h4 className="font-medium">Source & Additional Information</h4>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="source-of-info">Source of Information</Label>
                <Select value={formData.source_of_info} onValueChange={(value) => setFormData({...formData, source_of_info: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="How did you learn about this bid?" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="website">Client Website</SelectItem>
                    <SelectItem value="bidding-platform">Bidding Platform</SelectItem>
                    <SelectItem value="newspaper">Newspaper/Publication</SelectItem>
                    <SelectItem value="referral">Referral</SelectItem>
                    <SelectItem value="networking">Networking Event</SelectItem>
                    <SelectItem value="direct-invite">Direct Invitation</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="qualification-required">Pre-qualification Required</Label>
                <Select
                  value={formData.prequalification ? "true" : "false"}
                  onValueChange={(value) => setFormData({...formData, prequalification: value === "true"})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Is pre-qualification needed?" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="false">No</SelectItem>
                    <SelectItem value="true">Yes</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="detailed-description">Detailed Description</Label>
              <Textarea
                id="detailed-description"
                placeholder="Provide detailed description of the project scope, requirements, special conditions, etc..."
                rows={4}
                value={formData.project_description}
                onChange={(e) => setFormData({...formData, project_description: e.target.value})}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="special-requirements">Special Requirements/Notes</Label>
              <Textarea
                id="special-requirements"
                placeholder="Any special requirements, certifications needed, union requirements, etc..."
                rows={2}
                value={formData.special_req_notes}
                onChange={(e) => setFormData({...formData, special_req_notes: e.target.value})}
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Add Bid Opportunity'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Bid Details Modal (View/Edit)
function BidDetailsModal({ mode, bid, trigger, onSuccess }: { mode: 'view' | 'edit'; bid: any; trigger: React.ReactNode; onSuccess?: () => void }) {
  const orgCurrency = useOrgCurrency();
  const isReadOnly = mode === 'view';
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [documents, setDocuments] = useState<any[]>([]);
  const [errors, setErrors] = useState<{[key: string]: boolean}>({});
  const [formData, setFormData] = useState({
    project_name: '',
    bid_number: '',
    bid_package: '',
    contract_type: '',
    project_category: '',
    project_location: '',
    project_description: '',
    owner_name: '',
    owner_contact: '',
    contact_person: '',
    contact_email: '',
    client_owner_type: '',
    project_duration: '',
    currency: orgCurrency,
    total_bid_amount: '',
    base_bid_amount: '',
    bid_due_date: '',
    bid_opening_date: '',
    submission_location: '',
    bid_bond_required: false,
    bid_bond_percentage: '',
    bid_bond_amount: '',
    performance_bond_required: false,
    payment_bond_required: false,
    source_of_info: '',
    prequalification: false,
    special_req_notes: '',
    general_liability_required: false,
    workers_comp_required: false,
    prevailing_wage: false,
    mbe_wbe_requirements: '',
    disadvantaged_business_goals: '',
    environmental_requirements: '',
    safety_requirements: '',
    drug_testing_required: false,
    license_requirements: '',
    certification_requirements: '',
    union_requirements: '',
    architect_engineer: '',
    status: 'draft',
    show_mbe_wbe: false,
    show_disadvantaged_business: false,
    show_environmental: false,
    show_safety: false,
    show_license: false,
    show_certification: false,
    show_union: false
  });

  // Fetch bid details when modal opens
  useEffect(() => {
    const fetchBidDetails = async () => {
      if (!isOpen || !bid?.id) return;

      setLoading(true);
      setError(null);

      try {
        const userProfile = await authService.getUserProfile();
        const organizationId = userProfile.organizationId;

        if (import.meta.env.DEV) {
          console.log('Fetching bid details:', {
            bid_id: bid.id,
            endpoint: `/api/v1/bids/${bid.id}`,
            organization_id: organizationId
          });
        }

        const response = await httpClient.get(`/api/v1/bids/${bid.id}?organization_id=${organizationId}`);

        if (import.meta.env.DEV) {
          console.log('Bid details loaded:', response.data);
        }

        const data = response.data;

        // Populate form data from API response
        setFormData({
          project_name: data.project_name || '',
          bid_number: data.bid_number || '',
          bid_package: data.bid_package || '',
          contract_type: data.contract_type || '',
          project_category: data.project_category || '',
          project_location: data.project_location || '',
          project_description: data.project_description || '',
          owner_name: data.owner_name || '',
          owner_contact: data.owner_contact || '',
          contact_person: data.contact_person || '',
          contact_email: data.contact_email || '',
          client_owner_type: data.client_owner_type || '',
          project_duration: data.project_duration?.toString() || '',
          currency: data.currency || orgCurrency,
          total_bid_amount: data.total_bid_amount?.toString() || '',
          base_bid_amount: data.base_bid_amount?.toString() || '',
          bid_due_date: data.bid_due_date?.substring(0, 16) || '',
          bid_opening_date: data.bid_opening_date?.substring(0, 16) || '',
          submission_location: data.submission_location || '',
          bid_bond_required: data.bid_bond_required || false,
          bid_bond_percentage: data.bid_bond_percentage?.toString() || '',
          bid_bond_amount: data.bid_bond_amount?.toString() || '',
          performance_bond_required: data.performance_bond_required || false,
          payment_bond_required: data.payment_bond_required || false,
          source_of_info: data.source_of_info || '',
          prequalification: data.prequalification === true || data.prequalification === 'true' || data.prequalification === 'yes',
          special_req_notes: data.special_req_notes || '',
          general_liability_required: data.general_liability_required || false,
          workers_comp_required: data.workers_comp_required || false,
          prevailing_wage: data.prevailing_wage || false,
          mbe_wbe_requirements: data.mbe_wbe_requirements || '',
          disadvantaged_business_goals: data.disadvantaged_business_goals?.toString() || '',
          environmental_requirements: data.environmental_requirements || '',
          safety_requirements: data.safety_requirements || '',
          drug_testing_required: data.drug_testing_required || false,
          license_requirements: data.license_requirements || '',
          certification_requirements: data.certification_requirements || '',
          union_requirements: data.union_requirements || '',
          architect_engineer: data.architect_engineer || '',
          status: data.status || 'draft',
          show_mbe_wbe: !!(data.mbe_wbe_requirements),
          show_disadvantaged_business: !!(data.disadvantaged_business_goals),
          show_environmental: !!(data.environmental_requirements),
          show_safety: !!(data.safety_requirements),
          show_license: !!(data.license_requirements),
          show_certification: !!(data.certification_requirements),
          show_union: !!(data.union_requirements)
        });

        // Fetch documents if available
        if (data.documents_submitted && Array.isArray(data.documents_submitted)) {
          setDocuments(data.documents_submitted);
        }

      } catch (err: any) {
        console.error('Failed to fetch bid details:', err);
        setError(err.message || 'Failed to load bid details');
      } finally {
        setLoading(false);
      }
    };

    fetchBidDetails();
  }, [isOpen, bid?.id]);

  const handleBondTypeChange = (value: string) => {
    setFormData({
      ...formData,
      bid_bond_required: value === 'bid' || value === 'both' || value === 'all',
      performance_bond_required: value === 'performance' || value === 'both' || value === 'all',
      payment_bond_required: value === 'payment' || value === 'all'
    });
  };

  const handleSave = async () => {
    if (mode === 'view') return;

    // Validate required fields
    const validationErrors: {[key: string]: boolean} = {};

    if (!formData.project_name) validationErrors.project_name = true;
    if (!formData.bid_number) validationErrors.bid_number = true;

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      showError({ title: 'Validation Error', message: 'Please fill in all required fields highlighted in red' });
      return;
    }

    // Clear errors if validation passes
    setErrors({});
    setSaving(true);

    try {
      const userProfile = await authService.getUserProfile();
      const organizationId = userProfile.organizationId;

      // Prepare payload
      const payload = {
        project_name: formData.project_name,
        bid_number: formData.bid_number,
        bid_package: formData.bid_package,
        contract_type: formData.contract_type,
        project_category: formData.project_category,
        project_location: formData.project_location,
        project_description: formData.project_description,
        owner_name: formData.owner_name,
        owner_contact: formData.owner_contact,
        contact_person: formData.contact_person,
        contact_email: formData.contact_email,
        client_owner_type: formData.client_owner_type,
        project_duration: formData.project_duration ? parseInt(formData.project_duration) : null,
        currency: formData.currency,
        total_bid_amount: formData.total_bid_amount ? parseFloat(formData.total_bid_amount) : null,
        base_bid_amount: formData.base_bid_amount ? parseFloat(formData.base_bid_amount) : null,
        bid_due_date: formData.bid_due_date || null,
        bid_opening_date: formData.bid_opening_date || null,
        submission_location: formData.submission_location,
        bid_bond_required: formData.bid_bond_required,
        bid_bond_percentage: formData.bid_bond_percentage ? parseFloat(formData.bid_bond_percentage) : null,
        bid_bond_amount: formData.bid_bond_amount ? parseFloat(formData.bid_bond_amount) : null,
        performance_bond_required: formData.performance_bond_required,
        payment_bond_required: formData.payment_bond_required,
        source_of_info: formData.source_of_info,
        prequalification: formData.prequalification,
        special_req_notes: formData.special_req_notes,
        general_liability_required: formData.general_liability_required,
        workers_comp_required: formData.workers_comp_required,
        prevailing_wage: formData.prevailing_wage,
        mbe_wbe_requirements: formData.mbe_wbe_requirements,
        disadvantaged_business_goals: formData.disadvantaged_business_goals ? parseFloat(formData.disadvantaged_business_goals) : null,
        environmental_requirements: formData.environmental_requirements,
        safety_requirements: formData.safety_requirements,
        drug_testing_required: formData.drug_testing_required,
        license_requirements: formData.license_requirements,
        certification_requirements: formData.certification_requirements,
        union_requirements: formData.union_requirements,
        architect_engineer: formData.architect_engineer,
        status: formData.status
      };

      if (import.meta.env.DEV) {
        console.log('Updating bid:', {
          bid_id: bid.id,
          payload: payload
        });
      }

      await httpClient.put(`/api/v1/bids/${bid.id}?organization_id=${organizationId}`, payload);

      setIsOpen(false);
      showSuccess({ title: 'Bid Updated', message: 'Bid updated successfully!' });

      if (onSuccess) {
        onSuccess();
      }

    } catch (error) {
      console.error('Error updating bid:', error);
      showError({ title: 'Error', message: `Failed to update bid: ${error instanceof Error ? error.message : 'Unknown error'}` });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        {trigger}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto modal-content">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {mode === 'view' ? <Eye className="h-5 w-5" /> : <Edit className="h-5 w-5" />}
            {mode === 'view' ? 'View Bid Details' : 'Edit Bid Details'}
          </DialogTitle>
          <DialogDescription>
            {mode === 'view' ? 'View complete bid information' : 'Update bid information'}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : error ? (
          <div className="py-8 text-center">
            <p className="text-sm text-destructive">{error}</p>
          </div>
        ) : (
        <>
        <div className="space-y-6 py-4">
          {/* Project Information */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Building className="h-4 w-4 text-primary" />
              <h4 className="font-medium">Project Information</h4>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="project-name" className={errors.project_name ? 'text-red-600' : ''}>
                  Project Name <span className="text-red-600">*</span>
                </Label>
                <Input
                  id="project-name"
                  placeholder="e.g., Highway Bridge Construction"
                  value={formData.project_name}
                  onChange={(e) => {
                    setFormData({...formData, project_name: e.target.value});
                    if (errors.project_name) setErrors({...errors, project_name: false});
                  }}
                  disabled={isReadOnly}
                  className={errors.project_name ? 'border-red-600 focus-visible:ring-red-600' : ''}
                />
                {errors.project_name && <p className="text-xs text-red-600">Project name is required</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="bid-number" className={errors.bid_number ? 'text-red-600' : ''}>
                  Bid Number <span className="text-red-600">*</span>
                </Label>
                <Input
                  id="bid-number"
                  placeholder="e.g., BID-2024-001"
                  value={formData.bid_number}
                  onChange={(e) => {
                    setFormData({...formData, bid_number: e.target.value});
                    if (errors.bid_number) setErrors({...errors, bid_number: false});
                  }}
                  disabled={isReadOnly}
                  className={errors.bid_number ? 'border-red-600 focus-visible:ring-red-600' : ''}
                />
                {errors.bid_number && <p className="text-xs text-red-600">Bid number is required</p>}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="nature-of-project">Nature of Project</Label>
                <Select
                  value={formData.contract_type}
                  onValueChange={(value) => setFormData({...formData, contract_type: value})}
                  disabled={isReadOnly}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select project nature" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="new-construction">New Construction</SelectItem>
                    <SelectItem value="renovation">Renovation</SelectItem>
                    <SelectItem value="expansion">Expansion</SelectItem>
                    <SelectItem value="infrastructure">Infrastructure</SelectItem>
                    <SelectItem value="maintenance">Maintenance</SelectItem>
                    <SelectItem value="demolition">Demolition</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="project-category">Project Category</Label>
                <Select
                  value={formData.project_category}
                  onValueChange={(value) => setFormData({...formData, project_category: value})}
                  disabled={isReadOnly}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="residential">Residential</SelectItem>
                    <SelectItem value="commercial">Commercial</SelectItem>
                    <SelectItem value="industrial">Industrial</SelectItem>
                    <SelectItem value="infrastructure">Infrastructure</SelectItem>
                    <SelectItem value="healthcare">Healthcare</SelectItem>
                    <SelectItem value="education">Education</SelectItem>
                    <SelectItem value="government">Government</SelectItem>
                    <SelectItem value="mixed-use">Mixed-Use</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="project-location">Project Location</Label>
              <Input
                id="project-location"
                placeholder="e.g., Downtown Business District, City, State"
                value={formData.project_location}
                onChange={(e) => setFormData({...formData, project_location: e.target.value})}
                disabled={isReadOnly}
              />
            </div>
          </div>

          {/* Client Information */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <User className="h-4 w-4 text-primary" />
              <h4 className="font-medium">Client Information</h4>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="client-name">Client Name</Label>
                <Input
                  id="client-name"
                  placeholder="e.g., State Department of Transportation"
                  value={formData.owner_name}
                  onChange={(e) => setFormData({...formData, owner_name: e.target.value})}
                  disabled={isReadOnly}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="client-type">Client Type</Label>
                <Select
                  value={formData.client_owner_type}
                  onValueChange={(value) => setFormData({...formData, client_owner_type: value})}
                  disabled={isReadOnly}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select client type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="government">Government</SelectItem>
                    <SelectItem value="private">Private</SelectItem>
                    <SelectItem value="public">Public Entity</SelectItem>
                    <SelectItem value="non-profit">Non-Profit</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="contact-person">Contact Person</Label>
                <Input
                  id="contact-person"
                  placeholder="e.g., John Smith"
                  value={formData.owner_contact}
                  onChange={(e) => setFormData({...formData, owner_contact: e.target.value})}
                  disabled={isReadOnly}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contact-email">Contact Email</Label>
                <Input
                  id="contact-email"
                  type="email"
                  placeholder="e.g., john.smith@client.com"
                  value={formData.contact_email}
                  onChange={(e) => setFormData({...formData, contact_email: e.target.value})}
                  disabled={isReadOnly}
                />
              </div>
            </div>
          </div>

          {/* Project Timeline & Budget */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-primary" />
              <h4 className="font-medium">Project Timeline & Budget</h4>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="estimated-duration">Est. Duration (Months)</Label>
                <Input
                  id="estimated-duration"
                  type="number"
                  placeholder="12"
                  value={formData.project_duration}
                  onChange={(e) => setFormData({...formData, project_duration: e.target.value})}
                  disabled={isReadOnly}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="currency">Currency</Label>
                <Select
                  value={formData.currency}
                  onValueChange={(value) => setFormData({...formData, currency: value})}
                  disabled={isReadOnly}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CURRENCIES.map(c => <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="estimated-value">Estimated Value</Label>
                <Input
                  id="estimated-value"
                  type="number"
                  placeholder="4200000"
                  value={formData.total_bid_amount}
                  onChange={(e) => setFormData({...formData, total_bid_amount: e.target.value})}
                  disabled={isReadOnly}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="bid-deadline">Bid Submission Deadline</Label>
                <Input
                  id="bid-deadline"
                  type="datetime-local"
                  value={formData.bid_due_date}
                  onChange={(e) => setFormData({...formData, bid_due_date: e.target.value})}
                  disabled={isReadOnly}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="bid-opening-date">Bid Opening Date</Label>
                <Input
                  id="bid-opening-date"
                  type="datetime-local"
                  value={formData.bid_opening_date}
                  onChange={(e) => setFormData({...formData, bid_opening_date: e.target.value})}
                  disabled={isReadOnly}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="bond-required">Bond Required</Label>
              <Select
                onValueChange={handleBondTypeChange}
                disabled={isReadOnly}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select if bond required" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Bond Required</SelectItem>
                  <SelectItem value="bid">Bid Bond Only</SelectItem>
                  <SelectItem value="performance">Performance Bond Only</SelectItem>
                  <SelectItem value="payment">Payment Bond Only</SelectItem>
                  <SelectItem value="both">Bid & Performance Bond</SelectItem>
                  <SelectItem value="all">All Bonds Required</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {formData.bid_bond_required && (
              <div className="grid grid-cols-2 gap-4 pl-4 border-l-2 border-primary/20">
                <div className="space-y-2">
                  <Label htmlFor="bid-bond-percentage">Bid Bond Percentage (%)</Label>
                  <Input
                    id="bid-bond-percentage"
                    type="number"
                    placeholder="5"
                    value={formData.bid_bond_percentage}
                    onChange={(e) => setFormData({...formData, bid_bond_percentage: e.target.value})}
                    disabled={isReadOnly}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="bid-bond-amount">Bid Bond Amount</Label>
                  <Input
                    id="bid-bond-amount"
                    type="number"
                    placeholder="210000"
                    value={formData.bid_bond_amount}
                    onChange={(e) => setFormData({...formData, bid_bond_amount: e.target.value})}
                    disabled={isReadOnly}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Compliance Requirements */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-primary" />
              <h4 className="font-medium">Compliance Requirements</h4>
            </div>

            <div className="space-y-3 p-4 border rounded-lg">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="general-liability"
                  checked={formData.general_liability_required}
                  onChange={(e) => setFormData({...formData, general_liability_required: e.target.checked})}
                  disabled={isReadOnly}
                  className="h-4 w-4"
                />
                <Label htmlFor="general-liability" className="font-normal cursor-pointer">General Liability Insurance Required</Label>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="workers-comp"
                  checked={formData.workers_comp_required}
                  onChange={(e) => setFormData({...formData, workers_comp_required: e.target.checked})}
                  disabled={isReadOnly}
                  className="h-4 w-4"
                />
                <Label htmlFor="workers-comp" className="font-normal cursor-pointer">Workers Compensation Required</Label>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="prevailing-wage"
                  checked={formData.prevailing_wage}
                  onChange={(e) => setFormData({...formData, prevailing_wage: e.target.checked})}
                  disabled={isReadOnly}
                  className="h-4 w-4"
                />
                <Label htmlFor="prevailing-wage" className="font-normal cursor-pointer">Prevailing Wage</Label>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="drug-testing"
                  checked={formData.drug_testing_required}
                  onChange={(e) => setFormData({...formData, drug_testing_required: e.target.checked})}
                  disabled={isReadOnly}
                  className="h-4 w-4"
                />
                <Label htmlFor="drug-testing" className="font-normal cursor-pointer">Drug Testing Required</Label>
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="mbe-wbe-check"
                    checked={formData.show_mbe_wbe}
                    onChange={(e) => setFormData({...formData, show_mbe_wbe: e.target.checked})}
                    disabled={isReadOnly}
                    className="h-4 w-4 flex-shrink-0"
                  />
                  <Label htmlFor="mbe-wbe-check" className="font-normal cursor-pointer">MBE/WBE Requirements</Label>
                </div>
                {formData.show_mbe_wbe && (
                  <div className="pl-6 pr-2">
                    <Textarea
                      id="mbe-wbe"
                      placeholder="Enter MBE/WBE requirements..."
                      rows={2}
                      value={formData.mbe_wbe_requirements}
                      onChange={(e) => setFormData({...formData, mbe_wbe_requirements: e.target.value})}
                      disabled={isReadOnly}
                    />
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="disadvantaged-business-check"
                    checked={formData.show_disadvantaged_business}
                    onChange={(e) => setFormData({...formData, show_disadvantaged_business: e.target.checked})}
                    disabled={isReadOnly}
                    className="h-4 w-4 flex-shrink-0"
                  />
                  <Label htmlFor="disadvantaged-business-check" className="font-normal cursor-pointer">Disadvantaged Business Goals</Label>
                </div>
                {formData.show_disadvantaged_business && (
                  <div className="pl-6 pr-2">
                    <Label htmlFor="disadvantaged-business">Percentage (%)</Label>
                    <Input
                      id="disadvantaged-business"
                      type="number"
                      placeholder="0"
                      value={formData.disadvantaged_business_goals}
                      onChange={(e) => setFormData({...formData, disadvantaged_business_goals: e.target.value})}
                      disabled={isReadOnly}
                    />
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="environmental-check"
                    checked={formData.show_environmental}
                    onChange={(e) => setFormData({...formData, show_environmental: e.target.checked})}
                    disabled={isReadOnly}
                    className="h-4 w-4 flex-shrink-0"
                  />
                  <Label htmlFor="environmental-check" className="font-normal cursor-pointer">Environmental Requirements</Label>
                </div>
                {formData.show_environmental && (
                  <div className="pl-6 pr-2">
                    <Textarea
                      id="environmental-req"
                      placeholder="Enter environmental requirements..."
                      rows={2}
                      value={formData.environmental_requirements}
                      onChange={(e) => setFormData({...formData, environmental_requirements: e.target.value})}
                      disabled={isReadOnly}
                    />
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="safety-check"
                    checked={formData.show_safety}
                    onChange={(e) => setFormData({...formData, show_safety: e.target.checked})}
                    disabled={isReadOnly}
                    className="h-4 w-4 flex-shrink-0"
                  />
                  <Label htmlFor="safety-check" className="font-normal cursor-pointer">Safety Requirements</Label>
                </div>
                {formData.show_safety && (
                  <div className="pl-6 pr-2">
                    <Textarea
                      id="safety-req"
                      placeholder="Enter safety requirements..."
                      rows={2}
                      value={formData.safety_requirements}
                      onChange={(e) => setFormData({...formData, safety_requirements: e.target.value})}
                      disabled={isReadOnly}
                    />
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="license-check"
                    checked={formData.show_license}
                    onChange={(e) => setFormData({...formData, show_license: e.target.checked})}
                    disabled={isReadOnly}
                    className="h-4 w-4 flex-shrink-0"
                  />
                  <Label htmlFor="license-check" className="font-normal cursor-pointer">License Requirements</Label>
                </div>
                {formData.show_license && (
                  <div className="pl-6 pr-2">
                    <Textarea
                      id="license-req"
                      placeholder="Enter license requirements..."
                      rows={2}
                      value={formData.license_requirements}
                      onChange={(e) => setFormData({...formData, license_requirements: e.target.value})}
                      disabled={isReadOnly}
                    />
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="certification-check"
                    checked={formData.show_certification}
                    onChange={(e) => setFormData({...formData, show_certification: e.target.checked})}
                    disabled={isReadOnly}
                    className="h-4 w-4 flex-shrink-0"
                  />
                  <Label htmlFor="certification-check" className="font-normal cursor-pointer">Certification Requirements</Label>
                </div>
                {formData.show_certification && (
                  <div className="pl-6 pr-2">
                    <Textarea
                      id="certification-req"
                      placeholder="Enter certification requirements..."
                      rows={2}
                      value={formData.certification_requirements}
                      onChange={(e) => setFormData({...formData, certification_requirements: e.target.value})}
                      disabled={isReadOnly}
                    />
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="union-check"
                    checked={formData.show_union}
                    onChange={(e) => setFormData({...formData, show_union: e.target.checked})}
                    disabled={isReadOnly}
                    className="h-4 w-4 flex-shrink-0"
                  />
                  <Label htmlFor="union-check" className="font-normal cursor-pointer">Union Requirements</Label>
                </div>
                {formData.show_union && (
                  <div className="pl-6 pr-2">
                    <Textarea
                      id="union-req"
                      placeholder="Enter union requirements..."
                      rows={2}
                      value={formData.union_requirements}
                      onChange={(e) => setFormData({...formData, union_requirements: e.target.value})}
                      disabled={isReadOnly}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Source & Additional Info */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <FileText className="h-4 w-4 text-primary" />
              <h4 className="font-medium">Source & Additional Information</h4>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="source-of-info">Source of Information</Label>
                <Select
                  value={formData.source_of_info}
                  onValueChange={(value) => setFormData({...formData, source_of_info: value})}
                  disabled={isReadOnly}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="How did you learn about this bid?" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="website">Client Website</SelectItem>
                    <SelectItem value="bidding-platform">Bidding Platform</SelectItem>
                    <SelectItem value="newspaper">Newspaper/Publication</SelectItem>
                    <SelectItem value="referral">Referral</SelectItem>
                    <SelectItem value="networking">Networking Event</SelectItem>
                    <SelectItem value="direct-invite">Direct Invitation</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="qualification-required">Pre-qualification Required</Label>
                <Select
                  value={formData.prequalification ? "true" : "false"}
                  onValueChange={(value) => setFormData({...formData, prequalification: value === "true"})}
                  disabled={isReadOnly}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Is pre-qualification needed?" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="false">No</SelectItem>
                    <SelectItem value="true">Yes</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="detailed-description">Detailed Description</Label>
              <Textarea
                id="detailed-description"
                placeholder="Provide detailed description of the project scope, requirements, special conditions, etc..."
                rows={4}
                value={formData.project_description}
                onChange={(e) => setFormData({...formData, project_description: e.target.value})}
                disabled={isReadOnly}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="special-requirements">Special Requirements/Notes</Label>
              <Textarea
                id="special-requirements"
                placeholder="Any special requirements, certifications needed, union requirements, etc..."
                rows={2}
                value={formData.special_req_notes}
                onChange={(e) => setFormData({...formData, special_req_notes: e.target.value})}
                disabled={isReadOnly}
              />
            </div>
          </div>

          {/* Documents Section */}
          <div className="space-y-4">
            <DocumentList
              documents={documents}
              onError={(error) => {
                showError({ title: 'Download Failed', message: `Failed to download the document: ${error.message}` });
              }}
              emptyMessage="No documents available for this bid."
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setIsOpen(false)}>Cancel</Button>
          {mode === 'edit' && <Button onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>}
        </DialogFooter>
        </>
        )}
      </DialogContent>
    </Dialog>
    </>
  );
}

export function BidsTenders() {
  const { canWrite, canApprove, canDelete } = usePermission();
  const orgCurrency = useOrgCurrency();
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const { orgId } = useAuth();
  const { data: rawBids = [], isLoading: loading, error: bidsError, refetch } = useBids(orgId);
  const error = bidsError ? (bidsError as Error).message || 'Failed to load bids' : null;

  const refreshBids = () => { refetch(); };

  const bidOpportunities: any[] = useMemo(() => rawBids.map((bid: any) => ({
    id: bid.bid_id || bid.id,
    project: bid.project_name || bid.project,
    client: bid.owner_name || bid.client_name || bid.client || 'N/A',
    value: (() => {
      const amount = typeof bid.total_bid_amount === 'string' ? parseFloat(bid.total_bid_amount) : (bid.total_bid_amount || 0);
      return formatCurrencyCompact(isNaN(amount) ? 0 : amount, orgCurrency);
    })(),
    deadline: bid.bid_due_date || bid.submission_deadline || bid.deadline,
    status: bid.status || 'draft',
    category: bid.project_category || bid.project_type || bid.category || 'general',
    location: bid.project_location || bid.location || 'N/A',
    initiation: bid.project_start_date || bid.initiation,
    sourceInfo: bid.opportunity_source || 'N/A',
    bondRequired: bid.bid_bond_required && bid.performance_bond_required ? 'both'
                 : bid.bid_bond_required ? 'bid'
                 : bid.performance_bond_required ? 'performance'
                 : 'none',
    ...bid
  })), [rawBids]);

  const filteredBids = bidOpportunities.filter(bid => {
    const matchesSearch = bid.project?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         bid.client?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         bid.location?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === "all" || bid.status === statusFilter;
    const matchesCategory = categoryFilter === "all" || bid.category === categoryFilter;
    return matchesSearch && matchesStatus && matchesCategory;
  });

  const {
    currentPage: bidsCurrentPage,
    totalPages: bidsTotalPages,
    itemsPerPage: bidsItemsPerPage,
    paginatedData: paginatedBids,
    setCurrentPage: setBidsCurrentPage,
    setItemsPerPage: setBidsItemsPerPage
  } = usePagination({ data: filteredBids, initialItemsPerPage: 10 });

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      // Format as YYYY-MM-DD
      return date.toISOString().split('T')[0];
    } catch (error) {
      return dateString;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "draft": return <Badge className="bg-gray-100 text-gray-800">Draft</Badge>;
      case "submitted": return <Badge className="bg-blue-100 text-blue-800">Submitted</Badge>;
      case "under_review":
      case "under-review": return <Badge className="bg-yellow-100 text-yellow-800">Under Review</Badge>;
      case "accepted":
      case "won": return <Badge className="bg-green-600 text-white">Accepted</Badge>;
      case "rejected":
      case "lost": return <Badge variant="destructive">Rejected</Badge>;
      case "withdrawn": return <Badge className="bg-orange-100 text-orange-800">Withdrawn</Badge>;
      case "preparing": return <Badge variant="secondary">Preparing</Badge>;
      default: return <Badge variant="outline">{status}</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Bids & Tenders</h2>
          <p className="text-muted-foreground">Manage bidding process and tender submissions</p>
        </div>
        {canWrite('/bids') && <NewBidModal onSuccess={refreshBids} />}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Active Bids</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-700">
              {bidOpportunities.filter(b => ['submitted', 'under_review', 'under-review'].includes(b.status)).length}
            </div>
            <p className="text-xs text-muted-foreground">Currently bidding</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Won This Year</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-700">
              {(() => {
                const currentYear = new Date().getFullYear();
                const wonThisYear = bidOpportunities.filter(b => {
                  if (b.status !== 'accepted' && b.status !== 'won') return false;
                  if (!b.bid_opening_date && !b.updated_at && !b.created_at) return false;

                  const dateToCheck = b.bid_opening_date || b.updated_at || b.created_at;
                  const bidYear = new Date(dateToCheck).getFullYear();
                  return bidYear === currentYear;
                });
                return wonThisYear.length;
              })()}
            </div>
            <p className="text-xs text-muted-foreground">
              {(() => {
                const currentYear = new Date().getFullYear();
                const wonThisYear = bidOpportunities.filter(b => {
                  if (b.status !== 'accepted' && b.status !== 'won') return false;
                  if (!b.bid_opening_date && !b.updated_at && !b.created_at) return false;

                  const dateToCheck = b.bid_opening_date || b.updated_at || b.created_at;
                  const bidYear = new Date(dateToCheck).getFullYear();
                  return bidYear === currentYear;
                });
                const totalBids = bidOpportunities.length;
                return totalBids > 0 ? `${Math.round((wonThisYear.length / totalBids) * 100)}% win rate` : '0% win rate';
              })()}
            </p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Bid Pipeline</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-purple-700">
              {(() => {
                const activeBids = bidOpportunities.filter(b => ['submitted', 'under_review', 'under-review'].includes(b.status));
                const totalValue = activeBids.reduce((sum, bid) => {
                  const amount = bid.total_bid_amount || 0;
                  return sum + (typeof amount === 'number' ? amount : parseFloat(amount) || 0);
                }, 0);
                return formatCurrencyCompact(totalValue, orgCurrency);
              })()}
            </div>
            <p className="text-xs text-muted-foreground">Total potential value</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Avg. Response Time</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-emerald-700">
              {(() => {
                // Log bids with qualifying status to debug
                const qualifyingBids = bidOpportunities.filter(b =>
                  ['submitted', 'under_review', 'under-review', 'accepted', 'rejected', 'withdrawn'].includes(b.status)
                );

                if (qualifyingBids.length > 0) {
                  console.log('Qualifying bids for Avg Response Time:', qualifyingBids.map(b => ({
                    id: b.id,
                    status: b.status,
                    created_at: b.created_at,
                    submitted_at: b.submitted_at
                  })));
                }

                const submittedBids = bidOpportunities.filter(b => {
                  const hasSubmittedStatus = ['submitted', 'under_review', 'under-review', 'accepted', 'rejected', 'withdrawn'].includes(b.status);
                  const hasCreatedAt = b.created_at;
                  const hasSubmittedAt = b.submitted_at;
                  return hasSubmittedStatus && hasCreatedAt && hasSubmittedAt;
                });

                if (submittedBids.length === 0) return 'N/A';

                const totalDays = submittedBids.reduce((sum, bid) => {
                  const created = new Date(bid.created_at);
                  const submitted = new Date(bid.submitted_at);
                  const diffTime = Math.abs(submitted.getTime() - created.getTime());
                  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                  return sum + diffDays;
                }, 0);

                const avgDays = (totalDays / submittedBids.length).toFixed(1);
                return `${avgDays}d`;
              })()}
            </div>
            <p className="text-xs text-muted-foreground">Days to submit</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            Active Opportunities
          </CardTitle>
          <CardDescription>All bidding opportunities with advanced filtering</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input 
                  placeholder="Search bids, clients, or locations..." 
                  className="pl-9"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="submitted">Submitted</SelectItem>
                  <SelectItem value="under_review">Under Review</SelectItem>
                  <SelectItem value="accepted">Accepted</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                  <SelectItem value="withdrawn">Withdrawn</SelectItem>
                </SelectContent>
              </Select>
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  <SelectItem value="residential">Residential</SelectItem>
                  <SelectItem value="commercial">Commercial</SelectItem>
                  <SelectItem value="industrial">Industrial</SelectItem>
                  <SelectItem value="infrastructure">Infrastructure</SelectItem>
                  <SelectItem value="healthcare">Healthcare</SelectItem>
                  <SelectItem value="education">Education</SelectItem>
                  <SelectItem value="mixed-use">Mixed-Use</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {error && (
              <div className="p-4 border border-yellow-200 bg-yellow-50 rounded-lg">
                <p className="text-sm text-yellow-800">
                  <strong>Note:</strong> {error}. Showing demo data for reference.
                </p>
              </div>
            )}

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-center space-y-2">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                  <p className="text-sm text-muted-foreground">Loading bids...</p>
                </div>
              </div>
            ) : filteredBids.length === 0 ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-center space-y-2">
                  <p className="text-muted-foreground">No bids found</p>
                  <p className="text-sm text-muted-foreground">
                    {searchQuery || statusFilter !== "all" || categoryFilter !== "all"
                      ? "Try adjusting your filters"
                      : "Create your first bid to get started"}
                  </p>
                </div>
              </div>
            ) : (
              <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Project Name</TableHead>
                    <TableHead>Client</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Value</TableHead>
                    <TableHead>Deadline</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedBids.map((bid) => (
                  <TableRow key={bid.id}>
                    <TableCell className="font-medium">{bid.project}</TableCell>
                    <TableCell>{bid.client}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="capitalize text-xs">
                        {bid.category.replace('-', ' ')}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <MapPin className="h-3 w-3" />
                        {bid.location}
                      </div>
                    </TableCell>
                    <TableCell>{getStatusBadge(bid.status)}</TableCell>
                    <TableCell className="text-right font-medium">{bid.value}</TableCell>
                    <TableCell className="text-muted-foreground">{formatDate(bid.deadline)}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <BidDetailsModal
                          mode="view"
                          bid={bid}
                          onSuccess={refreshBids}
                          trigger={
                            <Button variant="ghost" size="sm" title="View Bid">
                              <Eye className="h-4 w-4" />
                            </Button>
                          }
                        />
                        {canWrite('/bids') && (
                          <BidDetailsModal
                            mode="edit"
                            bid={bid}
                            onSuccess={refreshBids}
                            trigger={
                              <Button variant="ghost" size="sm" title="Edit Bid">
                                <Edit className="h-4 w-4" />
                              </Button>
                            }
                          />
                        )}
                        {canWrite('/bids') && (
                          <BidSubmissionModal
                            bid={bid}
                            onSuccess={refreshBids}
                            trigger={
                              <Button variant="ghost" size="sm" title="Submit Bid">
                                <Upload className="h-4 w-4" />
                              </Button>
                            }
                          />
                        )}
                        <BidStatusUpdateModal
                          bid={bid}
                          onSuccess={refreshBids}
                          trigger={
                            <Button variant="ghost" size="sm" title="Update Status">
                              <Clock className="h-4 w-4" />
                            </Button>
                          }
                        />
                        {canApprove('/bids') && (
                          <ApprovalProcessModal
                            type="Bid"
                            item={bid}
                            onSuccess={refreshBids}
                            trigger={
                              <Button variant="ghost" size="sm" title="Bid Analysis">
                                <CheckCircle className="h-4 w-4" />
                              </Button>
                            }
                          />
                        )}
                      </div>
                    </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <Pagination
                currentPage={bidsCurrentPage}
                totalPages={bidsTotalPages}
                totalItems={filteredBids.length}
                itemsPerPage={bidsItemsPerPage}
                onPageChange={setBidsCurrentPage}
                onItemsPerPageChange={setBidsItemsPerPage}
              />
              </>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}